import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from '../CentralSnackbar';
import { createApiUrl } from '../../utils/apiConfig';
import Draggable from 'react-draggable';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Backdrop,
  CircularProgress,
  Alert,
  IconButton,
  Typography,
  Paper
} from '@mui/material';
import {
  Close as CloseIcon,
  Print as PrintIcon,
  Download as DownloadIcon,
  Fullscreen as FullscreenIcon
} from '@mui/icons-material';

// ====================== SMART TEXT RENDERER COMPONENT ======================
/**
 * Intelligente Text-Rendering-Komponente mit Zeilen-f├╝r-Zeilen-Clipping
 * Zeigt nur vollst├ñndige Zeilen an, die in die verf├╝gbare H├Âhe passen
 * Beh├ñlt HTML-Formatierungen (Farben, Gr├Â├ƒen, etc.) bei
 */
const SmartTextRenderer = ({ contentParts, maxHeight, maxWidth, fontSize, fontFamily, lineHeight, greetingText, signaturesCount, onContentMeasured }) => {
  const measureRef = useRef(null);
  const onContentMeasuredRef = useRef(onContentMeasured);
  const lastResultRef = useRef({ clippedContent: '', isOverflowing: false, remainingContent: '' });
  const [clippedContent, setClippedContent] = useState('');
  const [isOverflowing, setIsOverflowing] = useState(false);
  const [remainingContent, setRemainingContent] = useState('');
  
  // Update ref wenn sich callback ├ñndert
  useEffect(() => {
    onContentMeasuredRef.current = onContentMeasured;
  }, [onContentMeasured]);

  useEffect(() => {
    if (!contentParts || contentParts.length === 0 || !measureRef.current) return;
    
    // Berechne H├Âhe der Gru├ƒformel und ziehe sie von maxHeight ab
    let greetingHeightMm = 0;
    if (greetingText) {
      const greetingLines = greetingText.split('\n').length;
      const greetingHeightPx = (greetingLines * lineHeight) + (fontSize * 1.5);
      greetingHeightMm = greetingHeightPx / 3.7795275591;
    }
    
    // KRITISCH: Berechne Höhe des Unterschriften-Blocks (UNTEILBAR!)
    // Dieser Block muss KOMPLETT auf die Seite passen oder auf die nächste Seite umbrochen werden
    let signaturesBlockHeightMm = 0;
    if (signaturesCount > 0) {
      // Unterschriften-Block-Komponenten (OPTIMIERT):
      // - Grußformel ("Mit freundlichen kollegialen Grüßen"): ~6mm
      // - Leerzeile nach Grußformel: ~4mm  
      // - Unterschrift-Bild: 60px = ~16mm
      // - Name/Position/Job: 3 Zeilen à 3mm = ~9mm
      // - Elektronische Freigabe: 2 Zeilen à 3mm = ~6mm
      // - Margins/Abstände: ~6mm
      // - Sicherheits-Buffer: ~4mm
      const rowsNeeded = Math.ceil(signaturesCount / 3); // Grid mit 3 Spalten
      signaturesBlockHeightMm = 6 + 4 + (rowsNeeded * 25) + 6 + 4; // ~45mm für 1 Zeile, +25mm pro weitere Zeile
      
      console.log(`📏 OPTIMIERT - Unterschriften-Block-Höhe: ${signaturesCount} Unterzeichner (${rowsNeeded} Zeile(n)) = ${signaturesBlockHeightMm.toFixed(1)}mm`);
    }
    
    const adjustedMaxHeight = maxHeight - greetingHeightMm - signaturesBlockHeightMm;
    console.log(`📏 Body-Höhe für strukturierte Befunde: Layout-maxHeight=${maxHeight.toFixed(1)}mm - Grußformel=${greetingHeightMm.toFixed(1)}mm - Unterschriften=${signaturesBlockHeightMm.toFixed(1)}mm = ${adjustedMaxHeight.toFixed(1)}mm verfügbar für Content`);

    // Normalisiere Content Parts - rechne inline font-size Styles relativ um
    const editorBaseFontSize = 12; // KORRIGIERT: Echter Editor-Basis-Wert (nicht 14px!)
    const layoutBaseFontSize = fontSize;
    
    const normalizedContentParts = contentParts.map((part, index) => {
      let normalizedContent = part.content;
      if (part.content?.includes('font-size')) {
        normalizedContent = part.content.replace(/font-size:\s*(\d+)px;?/g, (match, sizePx) => {
          const originalSize = parseInt(sizePx);
          const diff = originalSize - editorBaseFontSize;
          const newSize = layoutBaseFontSize + diff;
          return `font-size: ${newSize}px;`;
        });
      }
      
      return {
        ...part,
        content: normalizedContent
      };
    });

    const fullContent = normalizedContentParts.map(part => {
      const labelHtml = part.label ? `<strong>${part.label}:</strong><br>` : '';
      return `${labelHtml}${part.content}`;
    }).join('<br><br>');

    // Konvertiere adjustedMaxHeight von mm zu px
    const maxHeightPx = adjustedMaxHeight * 3.7795275591;
    const maxWidthPx = maxWidth * 3.7795275591;
    
    // Schrittweise HTML rendern und messen
    const measureContainer = measureRef.current;
    measureContainer.innerHTML = '';
    
    // Kopiere Styles f├╝r genaue Messung
    measureContainer.style.fontSize = `${fontSize}px`;
    measureContainer.style.fontFamily = fontFamily;
    measureContainer.style.lineHeight = `${lineHeight}px`;
    measureContainer.style.position = 'absolute';
    measureContainer.style.visibility = 'hidden';
    measureContainer.style.width = maxWidth ? `${maxWidth}mm` : '100%';
    measureContainer.style.whiteSpace = 'normal';
    measureContainer.style.wordWrap = 'break-word';
    measureContainer.style.color = 'black';
    measureContainer.style.boxSizing = 'border-box';

    // Parse HTML in DOM und iteriere durch Child-Nodes
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = fullContent;
    
    // Erstelle einen Container f├╝r schrittweises Hinzuf├╝gen
    const testDiv = document.createElement('div');
    testDiv.style.cssText = measureContainer.style.cssText;
    measureContainer.appendChild(testDiv);
    
    let overflow = false;
    let lastValidHTML = '';
    
    // Funktion zum Hinzuf├╝gen von Nodes und Messen
    const addNodeAndMeasure = (node) => {
      const clonedNode = node.cloneNode(true);
      testDiv.appendChild(clonedNode);
      const height = testDiv.offsetHeight;
      
      if (height > maxHeightPx) {
        testDiv.removeChild(clonedNode);
        return false;
      }
      
      return true;
    };
    
    // Iteriere durch alle Child-Nodes
    const nodes = Array.from(tempDiv.childNodes);
    
    for (let i = 0; i < nodes.length; i++) {
      const node = nodes[i];
      
      // Versuche Node hinzuzuf├╝gen
      if (!addNodeAndMeasure(node)) {
        overflow = true;
        
        // Wenn es ein Text-Node ist, versuche Wort-f├╝r-Wort
        if (node.nodeType === Node.TEXT_NODE) {
          const text = node.textContent || '';
          const words = text.split(/\s+/);
          let partialText = '';
          
          for (const word of words) {
            const testText = partialText + (partialText ? ' ' : '') + word;
            const textNode = document.createTextNode(testText);
            testDiv.appendChild(textNode);
            
            if (testDiv.offsetHeight > maxHeightPx) {
              testDiv.removeChild(textNode);
              break;
            }
            
            partialText = testText;
          }
        }
        // Wenn es ein Element-Node ist, versuche Zeile-f├╝r-Zeile bei <br>
        else if (node.nodeType === Node.ELEMENT_NODE && node.innerHTML) {
          // F├╝r komplexe HTML-Elemente: versuche Kind-Elemente einzeln
          const childNodes = Array.from(node.childNodes);
          const clonedParent = node.cloneNode(false); // Nur Parent, ohne Kinder
          testDiv.appendChild(clonedParent);
          
          for (const childNode of childNodes) {
            const clonedChild = childNode.cloneNode(true);
            clonedParent.appendChild(clonedChild);
            
            if (testDiv.offsetHeight > maxHeightPx) {
              clonedParent.removeChild(clonedChild);
              break;
            }
          }
          
          // Falls Parent leer ist, entfernen
          if (clonedParent.childNodes.length === 0) {
            testDiv.removeChild(clonedParent);
          }
        }
        
        break;
      }
    }
    
    lastValidHTML = testDiv.innerHTML;
    
    // BERECHNE REMAINING CONTENT: Alles was nicht gerendert wurde
    let remainingHTML = '';
    if (overflow) {
      console.log('­ƒöì CLIPPING DEBUG:');
      console.log('  - MaxHeight (px):', maxHeightPx);
      console.log('  - Tats├ñchliche H├Âhe:', testDiv.offsetHeight);
      
      // NEUE BESSERE LOGIK: Vergleiche gerenderte innerHTML direkt mit Original
      // Dies funktioniert auch f├╝r Bilder und andere Nicht-Text-Elemente!
      const renderedHTML = testDiv.innerHTML;
      
      console.log('  - Gerendertes HTML L├ñnge:', renderedHTML.length);
      console.log('  - Volles HTML L├ñnge:', fullContent.length);
      
      // Erstelle neue tempDiv f├╝r remaining content
      const newTempDiv = document.createElement('div');
      newTempDiv.innerHTML = fullContent;
      
      const renderedNodes = Array.from(testDiv.childNodes);
      const allNodes = Array.from(newTempDiv.childNodes);
      
      console.log('  - Anzahl gerenderter Nodes:', renderedNodes.length);
      console.log('  - Anzahl aller Nodes:', allNodes.length);
      
      // Hilfsfunktion: Vergleicht zwei Nodes auf Gleichheit
      const nodesAreEqual = (node1, node2) => {
        if (!node1 || !node2) return false;
        if (node1.nodeType !== node2.nodeType) return false;
        
        if (node1.nodeType === Node.TEXT_NODE) {
          return node1.textContent === node2.textContent;
        } else if (node1.nodeType === Node.ELEMENT_NODE) {
          // Vergleiche Tag-Name und innerHTML
          if (node1.tagName !== node2.tagName) return false;
          
          // F├╝r Bilder: vergleiche src
          if (node1.tagName === 'IMG' && node2.tagName === 'IMG') {
            return node1.getAttribute('src') === node2.getAttribute('src');
          }
          
          // F├╝r andere Elemente: vergleiche innerHTML
          return node1.innerHTML === node2.innerHTML;
        }
        
        return false;
      };
      
      // Hilfsfunktion: Pr├╝ft ob ein Node teilweise gerendert wurde
      const getPartiallyRenderedContent = (originalNode, renderedNode) => {
        if (!originalNode || !renderedNode) return null;
        if (originalNode.nodeType !== Node.ELEMENT_NODE) return null;
        
        // Wenn innerHTML identisch ist, wurde nichts abgeschnitten
        if (originalNode.innerHTML === renderedNode.innerHTML) return null;
        
        // Der Node wurde teilweise gerendert - finde den Unterschied
        const originalChildren = Array.from(originalNode.childNodes);
        const renderedChildren = Array.from(renderedNode.childNodes);
        
        // Erstelle einen Clone des Original-Nodes
        const remainingNode = originalNode.cloneNode(false);
        
        // F├╝ge nur die nicht-gerenderten Child-Nodes hinzu
        let foundDifference = false;
        for (let i = 0; i < originalChildren.length; i++) {
          if (i < renderedChildren.length && !foundDifference) {
            // Pr├╝fe ob dieser Child-Node vollst├ñndig gerendert wurde
            if (nodesAreEqual(originalChildren[i], renderedChildren[i])) {
              continue; // Vollst├ñndig gerendert, ├╝berspringen
            } else {
              // Teilweise gerendert - rekursiv pr├╝fen
              const partialChild = getPartiallyRenderedContent(originalChildren[i], renderedChildren[i]);
              if (partialChild) {
                remainingNode.appendChild(partialChild);
              } else {
                // Wenn kein partial content, dann wurde dieser Node nicht gerendert
                remainingNode.appendChild(originalChildren[i].cloneNode(true));
              }
              foundDifference = true;
            }
          } else {
            // Dieser Child-Node wurde nicht gerendert
            remainingNode.appendChild(originalChildren[i].cloneNode(true));
            foundDifference = true;
          }
        }
        
        return remainingNode.childNodes.length > 0 ? remainingNode : null;
      };
      
      // Finde den Index, wo wir aufgeh├Ârt haben zu rendern
      let firstUnrenderedIndex = renderedNodes.length;
      let partialNode = null;
      
      // Pr├╝fe jeden gerenderten Node gegen den entsprechenden Original-Node
      for (let i = 0; i < renderedNodes.length; i++) {
        if (i >= allNodes.length) break;
        
        const renderedNode = renderedNodes[i];
        const originalNode = allNodes[i];
        
        if (!nodesAreEqual(renderedNode, originalNode)) {
          // Dieser Node wurde nur teilweise gerendert
          console.log(`  - Node ${i} wurde teilweise gerendert`);
          partialNode = getPartiallyRenderedContent(originalNode, renderedNode);
          firstUnrenderedIndex = i + 1;
          break;
        }
      }
      
      console.log('  - Erster nicht-gerenderter Index:', firstUnrenderedIndex);
      console.log('  - Partial Node vorhanden:', !!partialNode);
      
      // Baue remaining HTML zusammen
      const remainingDiv = document.createElement('div');
      
      // F├╝ge teilweise gerenderten Node hinzu (falls vorhanden)
      if (partialNode) {
        remainingDiv.appendChild(partialNode);
      }
      
      // F├╝ge alle verbleibenden Nodes hinzu
      for (let i = firstUnrenderedIndex; i < allNodes.length; i++) {
        remainingDiv.appendChild(allNodes[i].cloneNode(true));
      }
      
      remainingHTML = remainingDiv.innerHTML;
      
      console.log('  - Remaining HTML L├ñnge:', remainingHTML.length);
      console.log('  - Remaining Nodes:', remainingDiv.childNodes.length);
      
      // Debug: Zeige ersten Node-Typ im remaining content
      if (remainingDiv.childNodes.length > 0) {
        const firstNode = remainingDiv.childNodes[0];
        console.log('  - Erster Remaining Node Typ:', firstNode.nodeName, firstNode.nodeType);
        if (firstNode.nodeType === Node.ELEMENT_NODE && firstNode.tagName === 'IMG') {
          console.log('  - ­ƒû╝´©Å BILD gefunden in remaining content!');
        }
      }
    }
    
    // Nur setState wenn sich Werte ge├ñndert haben (verhindert Infinite Loop)
    if (lastResultRef.current.clippedContent !== lastValidHTML ||
        lastResultRef.current.isOverflowing !== overflow ||
        lastResultRef.current.remainingContent !== remainingHTML) {
      
      lastResultRef.current = { clippedContent: lastValidHTML, isOverflowing: overflow, remainingContent: remainingHTML };
      
      setClippedContent(lastValidHTML);
      setIsOverflowing(overflow);
      setRemainingContent(remainingHTML);
      
      // Callback mit allen Infos aufrufen (verwende ref statt direkten callback)
      if (onContentMeasuredRef.current) {
        onContentMeasuredRef.current({
          clippedContent: lastValidHTML,
          remainingContent: remainingHTML,
          isOverflowing: overflow,
          usedHeight: testDiv.offsetHeight,
          maxHeight: maxHeightPx
        });
      }
    }
    
  }, [contentParts, maxHeight, maxWidth, fontSize, fontFamily, lineHeight, greetingText, signaturesCount]);

  return (
    <>
      {/* Unsichtbarer Mess-Container */}
      <div
        ref={measureRef}
        style={{
          position: 'absolute',
          visibility: 'hidden',
          pointerEvents: 'none',
          top: 0,
          left: 0,
          width: '100%'
        }}
      />
      
      {/* Sichtbarer Content-Container */}
      <Box
        sx={{
          fontSize: `${fontSize}px`,
          fontFamily: fontFamily,
          lineHeight: `${lineHeight}px`,
          margin: 0,
          padding: 0,
          color: 'black',
          position: 'relative'
        }}
        dangerouslySetInnerHTML={{ __html: clippedContent }}
      />
      
      {/* Overflow-Indikator entfernt auf Benutzerwunsch */}
    </>
  );
};

// ====================== WATERMARK COMPONENT ======================
/**
 * Wasserzeichen-Komponente f├╝r nicht freigegebene Reports
 */
const Watermark = ({ text }) => {
  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 999,
        pointerEvents: 'none',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}
    >
      <Typography
        sx={{
          fontSize: '80px',
          fontWeight: 'bold',
          color: 'rgba(255, 0, 0, 0.15)',
          transform: 'rotate(-45deg)',
          textTransform: 'uppercase',
          whiteSpace: 'nowrap',
          userSelect: 'none',
          letterSpacing: '0.1em'
        }}
      >
        {text}
      </Typography>
    </Box>
  );
};

// ====================== FOLLOWING PAGE COMPONENT ======================
/**
 * Komponente f├╝r Folgeseiten (Page 2, 3, 4, ...)
 * Verwendet den "following" body area aus dem Layout
 * Rendert remainingContent vom SmartTextRenderer
 */
const FollowingPage = ({ remainingContent, layout, backgroundImage, fontSize, fontFamily, lineHeight, pageNumber, onNextPageOverflow, showWatermark, watermarkText, signaturesCount, highlightSearchTerms }) => {
  if (!remainingContent || remainingContent.trim().length === 0) {
    return null;
  }

  // Finde "following" body area
  const bodyArea = layout?.areas?.find(area => area.type === 'body' && area.page === 'following');
  if (!bodyArea) {
    console.error(`Keine "following" body area im Layout gefunden`);
    return null;
  }

  // Konvertiere Koordinaten
  const x = bodyArea.x * 0.5;
  const y = bodyArea.y * 0.5;
  const width = bodyArea.width * 0.5;
  const height = bodyArea.height * 0.5;
  const areaFontSize = bodyArea.fontSize || fontSize || 11;
  const areaFontFamily = bodyArea.fontFamily || fontFamily || '"Segoe UI"';
  const areaLineHeight = areaFontSize * 1.2;

  // Highlighting auf Folgeseiten anwenden
  const highlightedContent = highlightSearchTerms ? highlightSearchTerms(remainingContent) : remainingContent;

  const contentPartsForFollowing = [{
    label: '',
    content: highlightedContent,
    isDatabase: false,
    isHtml: true
  }];

  const handleContentMeasured = (measurement) => {
    if (measurement.isOverflowing && measurement.remainingContent && onNextPageOverflow) {
      onNextPageOverflow(measurement.remainingContent);
    }
  };

  return (
    <Paper
      sx={{
        width: '210mm',
        minHeight: '297mm',
        maxWidth: '100%',
        position: 'relative',
        backgroundColor: 'white',
        boxShadow: '3px 3px 12px rgba(0,0,0,0.15), 1px 1px 3px rgba(0,0,0,0.1)',
        border: 'none',
        outline: 'none',
        borderRadius: 0,
        overflow: 'hidden'
      }}
    >
      {/* Wasserzeichen f├╝r nicht freigegebene Reports */}
      {showWatermark && <Watermark text={watermarkText} />}

      {/* PDF-Hintergrund f├╝r Folgeseiten */}
      {backgroundImage && (() => {
        const pngUrl = backgroundImage.url.replace('/background/', '/background-png/');
        return (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              backgroundImage: `url(${pngUrl})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              opacity: 1.0
            }}
          />
        );
      })()}

      {/* Content Layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none',
          color: 'black',
          fontFamily: areaFontFamily
        }}
      >
        {/* Body Content */}
        <Box
          sx={{
            position: 'absolute',
            top: `${y}mm`,
            left: `${x}mm`,
            width: `${width}mm`,
            fontSize: `${areaFontSize}px`,
            fontFamily: areaFontFamily,
            lineHeight: `${areaLineHeight}px`,
            pointerEvents: 'auto'
          }}
        >
          <SmartTextRenderer
            contentParts={contentPartsForFollowing}
            maxHeight={height} // in mm
            maxWidth={width} // in mm
            fontSize={areaFontSize}
            fontFamily={areaFontFamily}
            lineHeight={areaLineHeight}
            greetingText="" // Keine Gru├ƒformel auf Folgeseiten
            signaturesCount={0} // Keine Unterschriften auf Folgeseiten
            onContentMeasured={handleContentMeasured}
          />
        </Box>
      </Box>
    </Paper>
  );
};

// ====================== ADDENDUM PAGE COMPONENT ======================
/**
 * Komponente für Addendum-Seiten
 * Jedes Addendum erhält eine eigene separate Seite
 */
const AddendumPage = ({ addendum, addendumIndex, layout, backgroundImage, fontSize, fontFamily, lineHeight, showWatermark, watermarkText, greetingEnd, t, createApiUrl, cacheBusterTimestamp, highlightSearchTerms }) => {
  if (!addendum || !addendum.approved_by) {
    return null;
  }

  // Finde "following" body area für Seitenränder
  const bodyArea = layout?.areas?.find(area => area.type === 'body' && area.page === 'following');
  if (!bodyArea) {
    console.error(`Keine "following" body area im Layout gefunden`);
    return null;
  }

  // Konvertiere Koordinaten (Seitenränder aus Layout)
  const x = bodyArea.x * 0.5;
  const y = bodyArea.y * 0.5;
  const width = bodyArea.width * 0.5;
  const areaFontSize = bodyArea.fontSize || fontSize || 11;
  const areaFontFamily = bodyArea.fontFamily || fontFamily || '"Segoe UI"';
  const areaLineHeight = areaFontSize * 1.2;

  // Formatiere Datum und Zeit
  let addendumDate = '';
  let addendumTime = '';
  if (addendum.created_at) {
    try {
      const date = new Date(addendum.created_at);
      addendumDate = date.toLocaleDateString('de-DE');
      addendumTime = date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      console.warn('Fehler beim Formatieren des Addendum-Datums:', addendum.created_at, e);
      addendumDate = addendum.created_at;
    }
  }
  
  // Highlighting auf Addendum Content anwenden
  const highlightedContent = highlightSearchTerms ? highlightSearchTerms(addendum.content || '') : (addendum.content || '');

  return (
    <Paper
      sx={{
        width: '210mm',
        minHeight: '297mm',
        maxWidth: '100%',
        position: 'relative',
        backgroundColor: 'white',
        boxShadow: '3px 3px 12px rgba(0,0,0,0.15), 1px 1px 3px rgba(0,0,0,0.1)',
        border: 'none',
        outline: 'none',
        borderRadius: 0,
        overflow: 'hidden'
      }}
    >
      {/* Wasserzeichen */}
      {showWatermark && <Watermark text={watermarkText} />}

      {/* PDF-Hintergrund */}
      {backgroundImage && (() => {
        const pngUrl = backgroundImage.url.replace('/background/', '/background-png/');
        return (
          <Box
            sx={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              zIndex: 1,
              backgroundImage: `url(${pngUrl})`,
              backgroundSize: 'cover',
              backgroundRepeat: 'no-repeat',
              backgroundPosition: 'center center',
              opacity: 1.0
            }}
          />
        );
      })()}

      {/* Content Layer */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 2,
          pointerEvents: 'none',
          color: 'black',
          fontFamily: areaFontFamily
        }}
      >
        {/* Addendum Content */}
        <Box
          sx={{
            position: 'absolute',
            top: `${y}mm`,
            left: `${x}mm`,
            width: `${width}mm`,
            fontSize: `${areaFontSize}px`,
            fontFamily: areaFontFamily,
            lineHeight: `${areaLineHeight}px`,
            pointerEvents: 'auto'
          }}
        >
          <Box
            sx={{
              fontSize: `${areaFontSize}px`,
              fontFamily: areaFontFamily,
              lineHeight: `${areaLineHeight}px`,
              margin: 0,
              padding: 0,
              color: 'black'
            }}
          >
            {/* Addendum Titel */}
            <Typography
              sx={{
                fontSize: `${areaFontSize}px`,
                fontFamily: areaFontFamily,
                lineHeight: `${areaLineHeight}px`,
                margin: 0,
                padding: 0,
                color: 'black',
                fontWeight: 'bold',
                marginBottom: '1em'
              }}
            >
              <strong>{t('pdfPreview.addendumDate', 'Addendum vom {{date}}, {{time}}', { date: addendumDate, time: addendumTime })}</strong>
            </Typography>

            {/* Addendum Inhalt */}
            <Typography
              sx={{
                fontSize: `${areaFontSize}px`,
                fontFamily: areaFontFamily,
                lineHeight: `${areaLineHeight}px`,
                margin: 0,
                padding: 0,
                color: 'black',
                whiteSpace: 'pre-wrap',
                marginBottom: '2em'
              }}
              dangerouslySetInnerHTML={{ __html: highlightedContent.trim() }}
            />

            {/* Leerzeile vor Grußformel */}
            <Typography
              sx={{
                fontSize: `${areaFontSize}px`,
                fontFamily: areaFontFamily,
                lineHeight: `${areaLineHeight}px`,
                margin: 0,
                padding: 0,
                color: 'black'
              }}
            >
              &nbsp;
            </Typography>

            {/* Grußformel */}
            {greetingEnd && (
              <Typography
                sx={{
                  fontSize: `${areaFontSize}px`,
                  fontFamily: areaFontFamily,
                  lineHeight: `${areaLineHeight}px`,
                  margin: 0,
                  padding: 0,
                  color: 'black',
                  marginBottom: '1em'
                }}
              >
                {greetingEnd}
              </Typography>
            )}

            {/* Unterschriften Grid */}
            {addendum.signatures_json && addendum.signatures_json.length > 0 && (
              <Box
                sx={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  gap: '10px',
                  marginTop: '1em',
                  marginBottom: '2em'
                }}
              >
                {addendum.signatures_json.map((signer, signerIndex) => {
                  const { user_id, academic_degree, first_name, last_name, position, job_title } = signer;
                  // Cache-Buster: Verwende fixierten Timestamp vom Dialog-Öffnen
                  const signatureUrl = createApiUrl(`profile/signature/${user_id}?t=${cacheBusterTimestamp}`);
                  
                  return (
                    <Box key={signerIndex} sx={{ textAlign: 'left' }}>
                      <img
                        src={signatureUrl}
                        alt={`Unterschrift ${first_name} ${last_name}`}
                        style={{
                          width: '140px',
                          height: '60px',
                          display: 'block',
                          margin: 0,
                          objectFit: 'contain'
                        }}
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                      <Typography sx={{ margin: '0.5em 0 0 0', fontSize: '0.9em' }}>
                        {position || '\u00A0'}
                      </Typography>
                      <Typography sx={{ margin: '0.2em 0 0 0', fontSize: '0.9em', fontWeight: 'bold' }}>
                        {academic_degree || ''} {first_name || ''} {last_name || ''}
                      </Typography>
                      <Typography sx={{ margin: '0.2em 0 0 0', fontSize: '0.85em' }}>
                        {job_title || ''}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            )}

            {/* Elektronische Freigabe */}
            {addendum.approval_json && (
              <Typography
                sx={{
                  fontSize: '0.9em',
                  fontStyle: 'italic',
                  margin: 0,
                  padding: 0,
                  color: 'black',
                  marginTop: '1em'
                }}
              >
                {t('pdfPreview.addendumElectronicApproval', 'Das Addendum wurde elektronisch freigegeben von {{title}} {{name}} am {{date}} um {{time}}.', {
                  title: addendum.approval_json.academic_degree || '',
                  name: `${addendum.approval_json.first_name || ''} ${addendum.approval_json.last_name || ''}`.trim(),
                  date: addendum.approval_json.approved_at || '',
                  time: addendum.approval_json.approved_time || ''
                })}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Paper>
  );
};

// ====================== MAIN DIALOG COMPONENT ======================
const PDFPreviewDialogStructured = ({ 
  open, 
  onClose, 
  pdfData, 
  reportTitle = "Befund-Vorschau",
  isPopupWindow = false,  // Neuer Prop für Popup-Fenster
  highlightTerms = []      // Suchbegriffe zum Hervorheben
}) => {
  const { t } = useTranslation();
  const { showInfo } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const pdfContainerRef = useRef(null); // WIEDER HINZUGEF├£GT f├╝r Backup-Code
  
  // Paper-Zoom (nur f├╝r das PDF-Paper, nicht ganzer Dialog)
  const [paperZoom, setPaperZoom] = useState(1);
  
  // Highlighting-Funktion für Suchbegriffe
  const highlightSearchTerms = (htmlContent) => {
    console.log('🔍 ===== HIGHLIGHTING DEBUG START (STRUCTURED) =====');
    console.log('🔍 highlightTerms:', highlightTerms);
    console.log('🔍 htmlContent length:', htmlContent?.length);
    console.log('🔍 htmlContent preview (first 500 chars):', htmlContent?.substring(0, 500));
    
    if (!htmlContent || !highlightTerms || highlightTerms.length === 0) {
      console.log('🔍 ❌ Skipping highlight - no content or no terms');
      return htmlContent;
    }
    
    // Erstelle temporäres DOM-Element zum Parsen
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlContent;
    
    console.log('🔍 Created tempDiv, childNodes count:', tempDiv.childNodes.length);
    
    let textNodesFound = 0;
    let textNodesModified = 0;
    
    // Funktion zum Highlighten in Text-Nodes
    const highlightInTextNode = (textNode) => {
      let text = textNode.textContent;
      let modified = false;
      
      console.log('🔍 Processing text node:', text.substring(0, 100));
      
      // Für jeden Suchbegriff
      highlightTerms.forEach(term => {
        if (!term || term.length === 0) return;
        
        console.log('🔍 Searching for term:', term);
        
        // Escape special regex characters im Suchbegriff
        const escapedTerm = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        
        // Erstelle Regex für case-insensitive Suche
        const regex = new RegExp(`(${escapedTerm})`, 'gi');
        
        console.log('🔍 Regex pattern:', regex);
        console.log('🔍 Testing regex against text...');
        
        if (regex.test(text)) {
          console.log('🔍 ✅ MATCH FOUND for term:', term);
          text = text.replace(
            regex,
            '<mark style="background-color: #FFB84D; padding: 2px 0; font-weight: normal;">$1</mark>'
          );
          modified = true;
        } else {
          console.log('🔍 ❌ NO MATCH for term:', term);
        }
      });
      
      if (modified) {
        console.log('🔍 ✅ Text node was modified, replacing...');
        // Erstelle neues Span-Element mit hervorgehobenem Text
        const span = document.createElement('span');
        span.innerHTML = text;
        textNode.parentNode.replaceChild(span, textNode);
        textNodesModified++;
      }
    };
    
    // Rekursive Funktion zum Durchlaufen aller Nodes
    const walkNodes = (node) => {
      if (node.nodeType === Node.TEXT_NODE) {
        // Text-Node gefunden - highlighten
        if (node.textContent.trim().length > 0) {
          textNodesFound++;
          highlightInTextNode(node);
        }
      } else if (node.nodeType === Node.ELEMENT_NODE) {
        // Skip <mark> tags (bereits highlighted)
        if (node.tagName !== 'MARK') {
          // Durchlaufe alle Child-Nodes
          Array.from(node.childNodes).forEach(child => walkNodes(child));
        }
      }
    };
    
    // Starte Highlighting
    walkNodes(tempDiv);
    
    console.log(`🔍 ===== HIGHLIGHTING SUMMARY (STRUCTURED) =====`);
    console.log(`🔍 Total text nodes found: ${textNodesFound}`);
    console.log(`🔍 Text nodes modified: ${textNodesModified}`);
    console.log(`🔍 Search terms: ${highlightTerms.join(', ')}`);
    console.log(`🔍 Result HTML preview (first 500 chars):`, tempDiv.innerHTML.substring(0, 500));
    console.log('🔍 ===== HIGHLIGHTING DEBUG END (STRUCTURED) =====');
    
    return tempDiv.innerHTML;
  };
  
  // ­ƒôä Multi-Page State: Array von followingPages mit remainingContent
  const [followingPages, setFollowingPages] = useState([]);
  
  // Cache-Buster Timestamp: Einmalig beim Öffnen setzen, um Endlosloop zu vermeiden
  const [cacheBusterTimestamp] = useState(() => new Date().getTime());
  
  // ­ƒÜ¿ Pr├╝fe ob Report nicht freigegeben ist ÔåÆ Wasserzeichen anzeigen
  const reportStatus = pdfData?.report?.status || pdfData?.report?.report?.status;
  const showWatermark = reportStatus && reportStatus !== 'APPROVED';
  const watermarkText = t('pdfPreview.watermark.notApproved', 'NICHT FREIGEGEBEN');

  // Browser-Zoom im Dialog verhindern
  useEffect(() => {
    if (!open) return;

    const preventBrowserZoom = (e) => {
      if (e.target.closest('[role="dialog"]') && (e.ctrlKey || e.metaKey)) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    const preventKeyboardZoom = (e) => {
      if (e.target.closest('[role="dialog"]') && (e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0')) {
        e.preventDefault();
        e.stopPropagation();
        return false;
      }
    };

    document.addEventListener('wheel', preventBrowserZoom, { passive: false, capture: true });
    document.addEventListener('keydown', preventKeyboardZoom, { passive: false, capture: true });

    return () => {
      document.removeEventListener('wheel', preventBrowserZoom, { capture: true });
      document.removeEventListener('keydown', preventKeyboardZoom, { capture: true });
    };
  }, [open]);

  // Live PDF Rendering Effect - f├╝r Option A deaktiviert
  useEffect(() => {
    if (pdfData && open) {
      setLoading(false); // Direkt im JSX gerendert
      // renderLivePDF(); // DEAKTIVIERT - wird jetzt direkt im JSX gemacht
    }
  }, [pdfData, open]);

  /* ===== BACKUP: ALTE IFRAME-IMPLEMENTIERUNG (f├╝r Rollback) =====
  // State f├╝r Dialog-Content Zoom
  const [dialogZoom, setDialogZoom] = useState(1);

  // Dialog-Content Zoom Handler - vereinfacht und direkter
  useEffect(() => {
    if (!open) return;
    
    console.log(`­ƒÄ» Zoom Event-Handler wird registriert f├╝r Dialog`);

    const handleDialogZoom = (e) => {
      // Debug: Zeige alle Wheel-Events
      if (e.ctrlKey || e.metaKey) {
        console.log(`­ƒöì Zoom-Event erkannt:`, { 
          ctrlKey: e.ctrlKey, 
          metaKey: e.metaKey, 
          deltaY: e.deltaY,
          target: e.target.tagName 
        });
        
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const delta = e.deltaY > 0 ? -0.1 : 0.1;
        const newZoom = Math.max(0.5, Math.min(3, dialogZoom + delta));
        setDialogZoom(newZoom);
        
        console.log(`­ƒöì Dialog-Zoom: ${(newZoom * 100).toFixed(0)}%`);
        return false;
      }
    };

    const handleKeyboardZoom = (e) => {
      if ((e.ctrlKey || e.metaKey) && (e.key === '+' || e.key === '-' || e.key === '0')) {
        console.log(`Ôî¿´©Å Keyboard-Zoom:`, { key: e.key, ctrlKey: e.ctrlKey, metaKey: e.metaKey });
        
        e.preventDefault();
        e.stopPropagation();
        
        let newZoom = dialogZoom;
        if (e.key === '+') {
          newZoom += 0.1;
        } else if (e.key === '-') {
          newZoom -= 0.1;
        } else if (e.key === '0') {
          newZoom = 1;
        }
        
        newZoom = Math.max(0.5, Math.min(3, newZoom));
        setDialogZoom(newZoom);
        console.log(`­ƒöì Dialog Keyboard-Zoom: ${(newZoom * 100).toFixed(0)}%`);
        
        return false;
      }
    };

    // Events direkt auf DialogContent setzen (um iframe zu umgehen)
    const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    const dialogElement = document.querySelector('[role="dialog"]');
    
    if (dialogContent || dialogElement) {
      const target = dialogContent || dialogElement;
      console.log('­ƒÄ» Event-Handler auf DialogContent/Dialog gesetzt:', target);
      
      target.addEventListener('wheel', handleDialogZoom, { passive: false, capture: true });
      target.addEventListener('keydown', handleKeyboardZoom, { passive: false, capture: true });
      target.style.outline = 'none'; // F├╝r Keyboard-Events
      target.tabIndex = -1; // Fokussierbar machen
      target.focus(); // Fokus setzen
      
      // Cleanup return function aktualisieren
      return () => {
        console.log(`­ƒº╣ Zoom Event-Handler wird entfernt`);
        target.removeEventListener('wheel', handleDialogZoom, { capture: true });
        target.removeEventListener('keydown', handleKeyboardZoom, { capture: true });
        setDialogZoom(1);
      };
    } else {
      console.log('ÔÜá´©Å Dialog-Element nicht gefunden, fallback auf Window');
      // Fallback auf Window falls Dialog nicht gefunden
      window.addEventListener('wheel', handleDialogZoom, { passive: false, capture: true });
      window.addEventListener('keydown', handleKeyboardZoom, { passive: false, capture: true });
      
      return () => {
        console.log(`­ƒº╣ Zoom Event-Handler (Window) wird entfernt`);
        window.removeEventListener('wheel', handleDialogZoom, { capture: true });
        window.removeEventListener('keydown', handleKeyboardZoom, { capture: true });
        setDialogZoom(1);
      };
    }
  }, [open, dialogZoom]);
  ===== ENDE BACKUP ===== */

  /* ===== BACKUP: ALTE RENDERING-FUNKTIONEN =====
  // Live PDF Rendering Function - BACKUP VERSION
  const renderLivePDF = async () => {
    setLoading(true);
    setError(null);
    
    try {
      console.log('­ƒÄ¿ Live PDF Rendering gestartet', pdfData);
      
      // Simuliere PDF-Rendering (hier w├╝rde sp├ñter echtes PDF-Rendering stattfinden)
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      if (pdfContainerRef.current) {
        // Leere den Container
        pdfContainerRef.current.innerHTML = '';
        
        // Erstelle PDF-├ñhnliche Ansicht
        const pdfPage = document.createElement('div');
        pdfPage.id = 'pdf-page-container';
        pdfPage.style.cssText = `
          width: 210mm;
          min-height: 297mm;
          background: white;
          margin: 20px auto;
          padding: 20mm;
          box-shadow: 3px 3px 12px rgba(0,0,0,0.15), 1px 1px 3px rgba(0,0,0,0.1);
          position: relative;
          font-family: Arial, sans-serif;
          line-height: 1.5;
          border: none;
          outline: none;
          border-radius: 0;
        `;
        
        // PDF-Hintergrundbild laden falls vorhanden
        const backgroundImages = pdfData.background_images;
        const firstPageUrl = backgroundImages?.firstPage?.url;
        
        if (firstPageUrl) {
          console.log('­ƒû╝´©Å PDF-Hintergrundbild wird geladen:', firstPageUrl);
          
          // PDF-Viewer Container um grauen Hintergrund zu verstecken
          const viewerContainer = document.createElement('div');
          viewerContainer.style.cssText = `
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            overflow: hidden;
            background: white;
            z-index: 1;
            opacity: 0.7;
          `;
          
          // PDF als Hintergrund ├╝ber iframe (vollst├ñndig eingebettet)
          const backgroundFrame = document.createElement('iframe');
          backgroundFrame.src = `${firstPageUrl}#toolbar=0&navpanes=0&scrollbar=0&statusbar=0&messages=0&view=FitH&zoom=page-fit&pagemode=none`;
          backgroundFrame.style.cssText = `
            position: absolute;
            top: -20px;
            left: -20px;
            width: calc(100% + 40px);
            height: calc(100% + 40px);
            border: none;
            outline: none;
            box-shadow: none;
            overflow: hidden;
            transform: scale(1);
            transform-origin: center center;
          `;
          
          // iframe komplett fixieren - kein Zoom/Scroll m├Âglich
          backgroundFrame.onload = function() {
            try {
              const iframeDoc = backgroundFrame.contentDocument || backgroundFrame.contentWindow.document;
              if (iframeDoc) {
                // Entferne alle Scrollm├Âglichkeiten
                iframeDoc.body.style.overflow = 'hidden';
                iframeDoc.documentElement.style.overflow = 'hidden';
                
                // Blockiere alle Interaktionen die Zoomen/Scrollen k├Ânnten
                const blockEvent = (e) => e.preventDefault();
                iframeDoc.addEventListener('wheel', blockEvent, { passive: false });
                iframeDoc.addEventListener('scroll', blockEvent, { passive: false });
                iframeDoc.addEventListener('touchmove', blockEvent, { passive: false });
                iframeDoc.addEventListener('keydown', (e) => {
                  // Blockiere alle Zoom/Scroll Shortcuts
                  if ((e.ctrlKey || e.metaKey) || 
                      ['PageUp', 'PageDown', 'Home', 'End', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                });
                
                console.log('­ƒöÆ PDF-Hintergrund vollst├ñndig fixiert');
              }
            } catch (e) {
              console.log('­ƒöÆ Cross-origin iframe - normale Browser-Sicherheit');
            }
          };
          
          // iframe f├╝r Events komplett blockieren (Text-Selektion later)
          backgroundFrame.style.pointerEvents = 'none';
          
          // Nach dem Laden nur Text-Selektion erlauben
          backgroundFrame.addEventListener('load', () => {
            setTimeout(() => {
              // Erlaube nur Text-Selektion, blockiere Zoom/Scroll
              backgroundFrame.style.pointerEvents = 'auto';
              backgroundFrame.addEventListener('wheel', (e) => {
                if (e.ctrlKey || e.metaKey) {
                  e.preventDefault();
                  e.stopPropagation();
                }
              }, { passive: false, capture: true });
            }, 100);
          });
          
          viewerContainer.appendChild(backgroundFrame);
          pdfPage.appendChild(viewerContainer);
          
          // TODO: Hier wird schrittweise der echte Befund-Inhalt aufgebaut
          console.log('­ƒôä PDF-Hintergrund bereit f├╝r Befund-Inhalt');
          
          // Event-Handler f├╝r PDF-Hintergrund Interaktivit├ñt 
          backgroundFrame.addEventListener('load', () => {
            console.log('Ô£à PDF-Hintergrund erfolgreich geladen');
            try {
              // PDF-Content ist nun ├╝ber iframe zug├ñnglich und selektierbar
              console.log('­ƒû▒´©Å PDF-Hintergrund ist interaktiv und selektierbar');
            } catch (e) {
              console.log('Ôä╣´©Å PDF-Hintergrund: Cross-origin Zugriff begrenzt (normal)');
            }
          });
          
        } else {
          // Fallback ohne Hintergrundbild
          pdfPage.innerHTML = `
            <div style="padding: 20mm;">
              <h1 style="margin: 0 0 10mm 0; color: #333; font-size: 18pt;">${reportTitle}</h1>
              <p style="margin: 3mm 0;"><strong>Report ID:</strong> ${pdfData.report.report_uid}</p>
              <p style="margin: 3mm 0;"><strong>Status:</strong> ${pdfData.report.status}</p>
              <p style="margin: 3mm 0;"><strong>Erstellt:</strong> ${new Date(pdfData.report.created_at).toLocaleDateString('de-DE')}</p>
              <p style="margin: 3mm 0;"><strong>Tenant ID:</strong> ${pdfData.tenant_id || 'Unbekannt'}</p>
              
              <div style="margin-top: 15mm; padding: 10mm; background: #fff3cd; border: 1px solid #ffc107; border-radius: 2mm;">
                <h3 style="margin: 0 0 5mm 0; color: #856404;">ÔÜá´©Å Live PDF Vorschau</h3>
                <p style="margin: 2mm 0;">­ƒôä Layout: ${pdfData.layout?.name || 'Kein Layout'}</p>
                <p style="margin: 2mm 0;">­ƒû╝´©Å Hintergrundbild: Nicht verf├╝gbar</p>
                ${pdfData.template ? `<p style="margin: 2mm 0; color: #666;">Legacy Template: ${pdfData.template.name}</p>` : ''}
              </div>
            </div>
          `;
        }
        
        pdfContainerRef.current.appendChild(pdfPage);
        console.log('Ô£à Live PDF Rendering abgeschlossen');
      }
      
      setLoading(false);
    } catch (err) {
      console.error('ÔØî Live PDF Rendering Fehler:', err);
      setError('Fehler beim Live-Rendern des PDFs');
      setLoading(false);
    }
  };

  // Handle PDF Print (Live-Version)
  const handlePrint = () => {
    if (!pdfContainerRef.current) return;
    
    // Erstelle Print-freundliche Version
    const printContent = pdfContainerRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>${reportTitle}</title>
            <style>
              body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
              @media print { 
                body { margin: 0; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };

  // Handle PDF Print (Live-Version) - BACKUP VERSION
  const handlePrint = () => {
    if (!pdfContainerRef.current) return;
    
    // Erstelle Print-freundliche Version
    const printContent = pdfContainerRef.current.innerHTML;
    const printWindow = window.open('', '_blank');
    
    if (printWindow) {
      printWindow.document.write(`
        <html>
          <head>
            <title>Befund-Druck</title>
            <style>
              body { margin: 0; padding: 20px; font-family: Arial, sans-serif; }
              @media print { 
                body { margin: 0; }
                .no-print { display: none !important; }
              }
            </style>
          </head>
          <body>
            ${printContent}
          </body>
        </html>
      `);
      printWindow.document.close();
      printWindow.focus();
      printWindow.print();
      printWindow.close();
    }
  };
  ===== ENDE BACKUP ===== */

  // BROWSER-NATIVE PDF - Hybrid-Ansatz: Layout-basiert + SmartTextRenderer-Content
  const handleDownloadBrowserNative = async () => {
    if (!pdfData) return;
    
    try {
      setLoading(true);
      
      // Berechne zentrierte Position f├╝r das Druckfenster - etwas h├Âher positioniert
      const windowWidth = 1200;
      const windowHeight = 900;
      const left = (window.screen.width - windowWidth) / 2;
      const top = Math.max(50, (window.screen.height - windowHeight) / 2 - 100); // 100px h├Âher, mindestens 50px vom oberen Rand
      
      const reportId = pdfData.report?.id || pdfData.report_id;
      const patientName = pdfData.report?.new_report?.new_exam?.patient_name || 'Patient';
      const patientNameSafe = patientName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Befund_${patientNameSafe}_${reportId}.pdf`;
      
      console.log(`­ƒû¿´©Å Browser-Native PDF (Hybrid-Ansatz) f├╝r Report ${reportId}`);
      
      // 1. Erstelle Print-Window - zentriert und in vern├╝nftiger Gr├Â├ƒe
      const windowFeatures = `width=${windowWidth},height=${windowHeight},left=${left},top=${top},scrollbars=yes,resizable=yes`;
      const printWindow = window.open('', '_blank', windowFeatures);
      if (!printWindow) {
        throw new Error('Pop-up blockiert - bitte Pop-ups f├╝r diese Seite erlauben');
      }
      
      // 2. Basis-HTML mit verbessertem CSS f├╝r Leerzeilen
      let printHTML = `
        <!DOCTYPE html>
        <html>
        <head>
          <meta charset="UTF-8">
          <title>${filename}</title>
          <style>
            @page {
              size: A4;
              margin: 0;
            }
            
            * {
              box-sizing: border-box;
              margin: 0;
              padding: 0;
              -webkit-print-color-adjust: exact !important;
              color-adjust: exact !important;
            }
            
            body {
              font-family: Helvetica, Arial, sans-serif;
              background: white;
              margin: 0;
              padding: 0;
              text-rendering: optimizeLegibility;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
              font-feature-settings: "kern" 1;
              text-size-adjust: 100%;
            }
            
            .pdf-page {
              width: 210mm;
              height: 297mm;
              position: relative;
              background: white;
              page-break-after: always;
              overflow: hidden;
              margin: 0;
              padding: 0;
              background-size: cover;
              background-repeat: no-repeat;
              background-position: center center;
            }
            
            .pdf-page:last-child {
              page-break-after: avoid;
            }
            
            /* Verbesserte Leerzeilen-Behandlung + TEXT-RENDERING-OPTIMIERUNG */
            p {
              margin: 0 0 1em 0;
              line-height: 13.2px;
              padding: 0;
              word-wrap: break-word;
              overflow-wrap: break-word;
              white-space: normal;
              text-rendering: optimizeLegibility;
              letter-spacing: 0.15008px;
              word-spacing: 0px;
            }
            
            p:empty, p:has(br:only-child) {
              margin: 0 0 1em 0;
              min-height: 1em;
            }
            
            br {
              line-height: 13.2px;
              display: block;
              margin: 0.5em 0;
            }
            
            div {
              margin: 0;
              padding: 0;
              line-height: 13.2px;
              word-wrap: break-word;
              overflow-wrap: break-word;
              letter-spacing: 0.15008px;
              word-spacing: 0px;
            }
            
            /* Globale Font-Rendering Kontrolle */
            * {
              box-sizing: border-box;
              text-rendering: optimizeLegibility;
              -webkit-font-smoothing: antialiased;
              -moz-osx-font-smoothing: grayscale;
            }
            
            /* Leerzeichen-Behandlung */
            p:contains("&nbsp;"), div:contains("&nbsp;") {
              min-height: 1em;
            }
            
            @media print {
              body { overflow: hidden !important; }
              * { overflow: visible !important; }
              p { margin: 0 0 1em 0 !important; }
              br { line-height: 1.2 !important; }
            }
          </style>
        </head>
        <body>
      `;
      
      // 3. Extrahiere geclippten Body-Content aus SmartTextRenderer
      const clippedBodyContent = await extractClippedBodyContent();
      
      // 4. Generiere erste Seite mit Layout-basierter Positionierung + geclipptem Content
      const backgroundImages = pdfData.background_images || {};
      const firstPageBackgroundUrl = backgroundImages.firstPage?.url?.replace('/background/', '/background-png/') || '';
      
      printHTML += `
        <div class="pdf-page" style="background-image: url('${firstPageBackgroundUrl}');">
          ${generateFirstPageWithClippedContent(clippedBodyContent.firstPage)}
        </div>
      `;
      
      // 5. Folgeseiten mit geclipptem Content
      if (clippedBodyContent.followingPages && clippedBodyContent.followingPages.length > 0) {
        const followingPageBackgroundUrl = backgroundImages.followingPages?.url?.replace('/background/', '/background-png/') || '';
        
        clippedBodyContent.followingPages.forEach(pageContent => {
          printHTML += `
            <div class="pdf-page" style="background-image: url('${followingPageBackgroundUrl}');">
              ${generateFollowingPageWithClippedContent(pageContent)}
            </div>
          `;
        });
      }
      
      printHTML += `
        </body>
        <script>
          window.onload = function() {
            setTimeout(function() {
              window.print();
              setTimeout(function() {
                window.close();
              }, 1000);
            }, 500);
          };
        </script>
        </html>
      `;
      
      // 6. Schreibe HTML ins Print-Window
      printWindow.document.write(printHTML);
      printWindow.document.close();
      
      console.log(`Ô£à Browser-Native PDF (Hybrid) gestartet`);
      
    } catch (error) {
      console.error('ÔØî Browser-Native PDF-Fehler:', error);
      showInfo(t('pdfPreview.downloadFailed', 'PDF-Download fehlgeschlagen: ') + error.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Hilfsfunktion: Extrahiert Body-Content 1:1 aus der Vorschau und bereinigt Leerzeilen
  const extractClippedBodyContent = async () => {
    const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
    const papers = dialogContent.querySelectorAll('.MuiPaper-root');
    
    const result = {
      firstPage: '',
      followingPages: []
    };
    
    // Hilfsfunktion: Bereinigt HTML und erh├ñlt Leerzeilen + korrigiert Schriftgr├Â├ƒen + entfernt Wasserzeichen (DOM-basiert)
    const cleanupHTML = (htmlContent, targetFontSize = 11) => {
      if (!htmlContent) return '';
      
      // ENTFERNE WASSERZEICHEN DOM-STRUKTURELL (NICHT text-basiert!) + DEBUG
      let cleaned = htmlContent;
      
      // DEBUG: Schaue nach Wasserzeichen-Pattern im HTML
      if (cleaned.includes('NICHT FREIGEGEBEN') || cleaned.includes('NOT APPROVED') || cleaned.includes('NON APPROUV├ë')) {
        console.log('­ƒöì DEBUG: Wasserzeichen-Text gefunden im HTML');
        
        // Finde das Pattern um das Wasserzeichen herum
        const lines = cleaned.split('\n');
        lines.forEach((line, index) => {
          if (line.includes('NICHT FREIGEGEBEN') || line.includes('NOT APPROVED') || line.includes('NON APPROUV├ë')) {
            console.log(`­ƒöì DEBUG Zeile ${index}: ${line.trim()}`);
            // Zeige auch die umgebenden Zeilen
            if (index > 0) console.log(`­ƒöì DEBUG Zeile ${index-1}: ${lines[index-1].trim()}`);
            if (index < lines.length - 1) console.log(`­ƒöì DEBUG Zeile ${index+1}: ${lines[index+1].trim()}`);
          }
        });
      }
      
      // Entferne Wasserzeichen basierend auf der DEBUG-Ausgabe
      const originalLength = cleaned.length;
      cleaned = cleaned
        .replace(/<div class="MuiBox-root css-8g66g0"><p[^>]*>NICHT FREIGEGEBEN<\/p><\/div>/gis, '') // Spezifisches Wasserzeichen-Pattern
        .replace(/<div class="MuiBox-root css-8g66g0"><p[^>]*>NOT APPROVED<\/p><\/div>/gis, '') // Englisch
        .replace(/<div class="MuiBox-root css-8g66g0"><p[^>]*>NON APPROUV├ë<\/p><\/div>/gis, '') // Franz├Âsisch
        .replace(/<div[^>]*z-index:\s*9\d{2}[^>]*>.*?<\/div>/gis, '') // z-index 900-999 (Fallback)
        .replace(/<div[^>]*z-index:\s*999[^>]*>.*?<\/div>/gis, '') // Explizit z-index 999 (Fallback)
        .replace(/<[^>]*style="[^"]*z-index:\s*9\d{2}[^"]*"[^>]*>.*?<\/[^>]*>/gis, '') // Inline z-index 900-999
        .replace(/<[^>]*transform:\s*rotate\(-45deg\)[^>]*>.*?<\/[^>]*>/gis, '') // Rotierte Elemente (typisch f├╝r Wasserzeichen)
        .replace(/<[^>]*transform:\s*rotate\([^)]*45deg[^)]*\)[^>]*>.*?<\/[^>]*>/gis, ''); // Beliebige 45┬░ Rotation
      
      const removedChars = originalLength - cleaned.length;
      
            // SCHRIFTGR├ûSSEN-KORREKTUR - ANGEPASST AN ECHTE EDITOR-BASIS!
            const editorBaseFontSize = 12; // KORRIGIERT: Echter Editor-Basis-Wert (nicht 14px!)
            const layoutBaseFontSize = targetFontSize; // Ziel-Schriftgr├Â├ƒe f├╝r PDF
            
            // Korrigiere inline font-size Styles - ANGEPASST AN ECHTE EDITOR-LOGIK!
            if (cleaned.includes('font-size')) {
              cleaned = cleaned.replace(/font-size:\s*(\d+)px;?/g, (match, sizePx) => {
                const originalSize = parseInt(sizePx);
                // KORRIGIERTE LOGIK: Editor-Basis ist 12px (nicht 14px!)
                const diff = originalSize - editorBaseFontSize;
                const newSize = layoutBaseFontSize + diff;
                console.log(`­ƒöñ KORRIGIERTE EDITOR-LOGIK: ${originalSize}px -> ${newSize}px (diff: ${diff}, editorBase: ${editorBaseFontSize}px, layout: ${layoutBaseFontSize}px)`);
                return `font-size: ${newSize}px; letter-spacing: 0.15008px;`;
              });
            }
      
            // Ersetze mehrfache <br> durch Paragraph-Breaks + Gru├ƒformel-Fix
            cleaned = cleaned
              .replace(/<br\s*\/?>\s*<br\s*\/?>/gi, '</p><p>')  // Doppelte <br> -> Absatzwechsel
              .replace(/<br\s*\/?>/gi, '<br>')  // Normalisiere <br> Tags
              .replace(/<p>\s*<\/p>/gi, '<p>&nbsp;</p>')  // Leere Paragraphen -> Leerzeile
              .replace(/<div>\s*<\/div>/gi, '<div>&nbsp;</div>')  // Leere Divs -> Leerzeile
              .replace(/\s*<\/p>\s*<p>\s*/gi, '</p><p>')  // Bereinige Paragraph-├£berg├ñnge
              .replace(/^\s*<p>/, '<p>')  // Entferne f├╝hrende Leerzeichen
              .replace(/<\/p>\s*$/, '</p>')  // Entferne nachfolgende Leerzeichen
            // VERBESSERTE Gru├ƒformel-Regex - flexibler und robuster + DEBUG
            const originalForGreeting = cleaned;
            cleaned = cleaned
              .replace(/(Sehr geehrte[rs]?\s+[^,]+,)(\s*)/gi, (match, greeting, whitespace) => {
                console.log(`­ƒÄ» GRUSSFORMEL gefunden: "${greeting}" mit Whitespace: "${whitespace}"`);
                return `${greeting}</p><p>&nbsp;</p><p>`;
              })
              .replace(/(Mit freundlichen Gr├╝├ƒen[^<]*)/gi, (match, farewell) => {
                console.log(`­ƒæï ABSCHIEDSGRUSS gefunden: "${farewell}"`);
                return `</p><p>&nbsp;</p><p>${farewell}`;
              })
              .replace(/(Hochachtungsvoll[^<]*)/gi, (match, farewell) => {
                console.log(`­ƒæï HOCHACHTUNGSVOLL gefunden: "${farewell}"`);
                return `</p><p>&nbsp;</p><p>${farewell}`;
              })
              .replace(/(Beste Gr├╝├ƒe[^<]*)/gi, (match, farewell) => {
                console.log(`­ƒæï BESTE GR├£SSE gefunden: "${farewell}"`);
                return `</p><p>&nbsp;</p><p>${farewell}`;
              });
            
            if (originalForGreeting !== cleaned) {
              console.log(`­ƒÄ» Gru├ƒformel-Transformationen angewendet!`);
            } else {
              console.log(`ÔÜá´©Å KEINE Gru├ƒformel-Pattern gefunden im Text!`);
              // DEBUG: Zeige ersten Teil des Textes
              const firstChars = cleaned.substring(0, 500);
              console.log(`­ƒöì Text-Anfang (erste 500 Zeichen):`, firstChars);
            }
      
      console.log(`­ƒº╣ Wasserzeichen DOM-strukturell entfernt (sprachunabh├ñngig)`);
      return cleaned;
    };
    
    // Erste Seite: Finde den Body-Container (vereinfacht - NICHT zu restriktiv!)
    if (papers[0]) {
      const allBoxes = papers[0].querySelectorAll('.MuiBox-root');
      let bodyContainer = null;
      let maxTop = 0;
      
      // Suche nach dem gr├Â├ƒten Container weiter unten (wie vorher)
      allBoxes.forEach(box => {
        const computedStyle = window.getComputedStyle(box);
        
        if (computedStyle.position === 'absolute') {
          const top = parseFloat(computedStyle.top);
          const height = parseFloat(computedStyle.height);
          const width = parseFloat(computedStyle.width);
          
          // Body-Container: weiter unten, angemessene Gr├Â├ƒe, breit genug
          if (top > maxTop && height > 50 && height < 800 && width > 400) {
            bodyContainer = box;
            maxTop = top;
            console.log(`­ƒÄ» Body-Container gefunden: ${width.toFixed(0)}x${height.toFixed(0)}px bei top:${top.toFixed(0)}px`);
          }
        }
      });
      
      if (bodyContainer) {
        const rawHTML = bodyContainer.innerHTML;
        
              // VOLLST├äNDIGE RENDERING-DEBUG-ANALYSE ­ƒöì
              const domStyle = window.getComputedStyle(bodyContainer);
              const domWidthPx = parseFloat(domStyle.width);
              const domWidthMm = domWidthPx / 3.7795275591; // px zu mm
              
              console.log(`­ƒöì ========== RENDERING-DEBUG-ANALYSE ==========`);
              console.log(`­ƒôÅ DOM-BREITEN-VERGLEICH:`);
              console.log(`­ƒôÅ Preview DOM: ${domWidthPx.toFixed(1)}px = ${domWidthMm.toFixed(1)}mm`);
              
              // Hole die Ziel-Schriftgr├Â├ƒe aus dem Body-Layout
              const bodyArea = pdfData.layout?.areas?.find(area => area.type === 'body' && area.page === 'first');
              const targetFontSize = bodyArea?.fontSize || 11;
              const layoutWidthMm = bodyArea ? (bodyArea.width * 0.5) : 0;
              
              console.log(`­ƒôÅ Layout Area: ${bodyArea?.width || 0}pts = ${layoutWidthMm.toFixed(1)}mm`);
              console.log(`­ƒôÅ DIFFERENZ: ${Math.abs(domWidthMm - layoutWidthMm).toFixed(1)}mm`);
              
              // ERWEITERTE RENDERING-EIGENSCHAFTEN DEBUG ­ƒÄ»
              console.log(`­ƒöñ FONT-EIGENSCHAFTEN:`);
              console.log(`­ƒöñ fontSize: ${domStyle.fontSize} (computed)`);
              console.log(`­ƒöñ fontFamily: ${domStyle.fontFamily}`);
              console.log(`­ƒöñ fontWeight: ${domStyle.fontWeight}`);
              console.log(`­ƒöñ lineHeight: ${domStyle.lineHeight}`);
              console.log(`­ƒöñ letterSpacing: ${domStyle.letterSpacing}`);
              console.log(`­ƒöñ wordSpacing: ${domStyle.wordSpacing}`);
              
              console.log(`­ƒôª BOX-MODEL:`);
              console.log(`­ƒôª width: ${domStyle.width}`);
              console.log(`­ƒôª height: ${domStyle.height}`);
              console.log(`­ƒôª padding: ${domStyle.padding}`);
              console.log(`­ƒôª margin: ${domStyle.margin}`);
              console.log(`­ƒôª border: ${domStyle.border}`);
              console.log(`­ƒôª boxSizing: ${domStyle.boxSizing}`);
              
              console.log(`­ƒöä TEXT-RENDERING:`);
              console.log(`­ƒöä textRendering: ${domStyle.textRendering}`);
              console.log(`­ƒöä fontSmooth: ${domStyle.webkitFontSmoothing}`);
              console.log(`­ƒöä textSizeAdjust: ${domStyle.textSizeAdjust}`);
              
              console.log(`­ƒôØ WORD-WRAP & OVERFLOW:`);
              console.log(`­ƒôØ wordWrap: ${domStyle.wordWrap}`);
              console.log(`­ƒôØ wordBreak: ${domStyle.wordBreak}`);
              console.log(`­ƒôØ overflowWrap: ${domStyle.overflowWrap}`);
              console.log(`­ƒôØ whiteSpace: ${domStyle.whiteSpace}`);
              console.log(`­ƒôØ overflow: ${domStyle.overflow}`);
              
              // TESTE EINEN SAMPLE TEXT F├£R GENAUE MESSUNG
              const sampleText = bodyContainer.querySelector('p, div');
              if (sampleText) {
                const sampleStyle = window.getComputedStyle(sampleText);
                console.log(`­ƒôè SAMPLE-TEXT-ELEMENT:`);
                console.log(`­ƒôè fontSize: ${sampleStyle.fontSize}`);
                console.log(`­ƒôè lineHeight: ${sampleStyle.lineHeight}`);
                console.log(`­ƒôè fontFamily: ${sampleStyle.fontFamily}`);
                console.log(`­ƒôè letterSpacing: ${sampleStyle.letterSpacing}`);
                console.log(`­ƒôè wordSpacing: ${sampleStyle.wordSpacing}`);
              }
              
              console.log(`­ƒöì ===============================================`);
        
          result.firstPage = cleanupHTML(rawHTML, targetFontSize);
          
          // SPEICHERE EXAKTE PREVIEW-STYLES f├╝r Container-Anpassung
          const exactPreviewStyles = {
            fontSize: domStyle.fontSize,
            fontFamily: domStyle.fontFamily,
            fontWeight: domStyle.fontWeight, 
            lineHeight: domStyle.lineHeight,
            letterSpacing: domStyle.letterSpacing,
            wordSpacing: domStyle.wordSpacing,
            textRendering: domStyle.textRendering,
            wordWrap: domStyle.wordWrap,
            wordBreak: domStyle.wordBreak,
            overflowWrap: domStyle.overflowWrap,
            whiteSpace: domStyle.whiteSpace
          };
          
          // Mache Preview-Styles global verf├╝gbar
          window.exactPreviewStyles = exactPreviewStyles;
          
          console.log(`­ƒöì Erste Seite Body-Content: ${result.firstPage.length} Zeichen (Schriftgr├Â├ƒe: ${targetFontSize}px)`);
          console.log(`­ƒÄ» EXAKTE PREVIEW-STYLES gespeichert:`, exactPreviewStyles);
      } else {
        console.warn('ÔÜá´©Å Kein Body-Container gefunden');
        result.firstPage = '';
      }
    }
    
        // Folgeseiten: 1:1 Kopie mit Bereinigung und Schriftgr├Â├ƒen-Korrektur (+2px wie erste Seite!)
        for (let i = 1; i < papers.length; i++) {
          const rawContent = papers[i].innerHTML;
          if (rawContent) {
            // Hole die Ziel-Schriftgr├Â├ƒe f├╝r Folgeseiten
            const followingBodyArea = pdfData.layout?.areas?.find(area => area.type === 'body' && area.page === 'following');
            const targetFontSize = followingBodyArea?.fontSize || 11;
            
            const cleanedContent = cleanupHTML(rawContent, targetFontSize);
            result.followingPages.push(cleanedContent);
            console.log(`­ƒöì Folgeseite ${i} Content bereinigt: ${cleanedContent.length} Zeichen (Schriftgr├Â├ƒe: ${targetFontSize}px + 2px = ${targetFontSize + 2}px)`);
          }
        }
    
    return result;
  };
  
  // Hilfsfunktion: Generiert erste Seite mit Layout + geclipptem Content
  const generateFirstPageWithClippedContent = (clippedBodyContent) => {
    let content = '';
    
    // Wasserzeichen f├╝r nicht freigegebene Reports
    if (showWatermark) {
      content += `
        <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 999; 
                    display: flex; justify-content: center; align-items: center; pointer-events: none;">
          <div style="font-size: 80px; font-weight: bold; color: rgba(255, 0, 0, 0.15); 
                      transform: rotate(-45deg); text-transform: uppercase; white-space: nowrap; 
                      user-select: none; letter-spacing: 0.1em;">
            ${watermarkText}
          </div>
        </div>
      `;
    }
    
    // Briefkopf (Layout-basiert)
    if (pdfData.layout?.areas && pdfData.address) {
      const letterheadArea = pdfData.layout.areas.find(area => area.type === 'letterhead');
      if (letterheadArea) {
        const x = letterheadArea.x * 0.5;
        const y = letterheadArea.y * 0.5;
        const width = letterheadArea.width * 0.5;
        const fontSize = letterheadArea.fontSize || 12;
        const fontFamily = 'Helvetica, Arial, sans-serif';
        const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px
        
        const addressLines = [];
        const addr = pdfData.address;
        
        if (addr.organization?.trim()) addressLines.push(addr.organization.trim());
        
        const nameParts = [addr.salutation, addr.title, addr.first_name, addr.last_name]
          .filter(part => part?.trim()).map(part => part.trim());
        if (nameParts.length > 0) addressLines.push(nameParts.join(' '));
        
        if (addr.street?.trim()) addressLines.push(addr.street.trim());
        
        const cityParts = [addr.postal_code, addr.city]
          .filter(part => part?.trim()).map(part => part.trim());
        if (cityParts.length > 0) addressLines.push(cityParts.join(' '));
        
        if (addr.country?.trim()) addressLines.push(addr.country.trim());
        
        content += `
          <div style="position: absolute; top: ${y}mm; left: ${x}mm; width: ${width}mm; 
                      font-size: ${fontSize}px; font-family: ${fontFamily}; line-height: ${lineHeight}px;">
            ${addressLines.map(line => `<div style="margin: 0; padding: 0; color: black;">${line}</div>`).join('')}
          </div>
        `;
      }
    }
    
    // Patienteninfo (Layout-basiert)
    if (pdfData.layout?.areas) {
      const patientinfoArea = pdfData.layout.areas.find(area => area.type === 'patientinfo');
      if (patientinfoArea) {
        const x = patientinfoArea.x * 0.5;
        const y = patientinfoArea.y * 0.5;
        const width = patientinfoArea.width * 0.5;
        const fontSize = patientinfoArea.fontSize || 11;
        const fontFamily = 'Helvetica, Arial, sans-serif';
        const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px
        
        const patientData = pdfData.report?.new_report?.new_exam;
        if (patientData) {
          let formattedBirthDate = patientData.birth_date || '[Geburtsdatum]';
          if (patientData.birth_date) {
            try {
              const birthDate = new Date(patientData.birth_date);
              formattedBirthDate = birthDate.toLocaleDateString('de-DE');
            } catch (e) {
              console.warn('Fehler beim Formatieren des Geburtsdatums:', patientData.birth_date, e);
            }
          }
          
          const sex = patientData.sex;
          let genderSpecificText = sex === 'M' ? 'Patient' : sex === 'F' ? 'Patientin' : 'Patient';
          
          const patientText = `${genderSpecificText}: ${patientData.patient_name || '[Name, Vorname]'}, *${formattedBirthDate}, Patienten-ID ${patientData.patient_id || '[patient_id]'}`;
          
          content += `
            <div style="position: absolute; top: ${y}mm; left: ${x}mm; width: ${width}mm; 
                        font-size: ${fontSize}px; font-family: ${fontFamily}; line-height: ${lineHeight}px; 
                        font-weight: bold; color: black; margin: 0; padding: 0;">
              ${patientText}
            </div>
          `;
        }
      }
    }
    
    // Betreff (Layout-basiert)
    if (pdfData.layout?.areas) {
      const subjectArea = pdfData.layout.areas.find(area => area.type === 'subject');
      if (subjectArea) {
        const x = subjectArea.x * 0.5;
        const y = subjectArea.y * 0.5;
        const width = subjectArea.width * 0.5;
        const fontSize = subjectArea.fontSize || 12;
        const fontFamily = 'Helvetica, Arial, sans-serif';
        const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px
        
        const examData = pdfData.report?.new_report?.new_exam;
        const studyDate = examData?.studydate || examData?.studyDate || examData?.study_date;
        const modality = examData?.modality || 'CT';
        
        let formattedDate = 'Datum';
        if (studyDate) {
          try {
            const date = new Date(studyDate);
            if (!isNaN(date.getTime())) {
              formattedDate = date.toLocaleDateString('de-DE');
            } else {
              formattedDate = studyDate.toString();
            }
          } catch (e) {
            formattedDate = studyDate.toString();
          }
        }
        
        const subjectText = `${modality}-Untersuchung vom ${formattedDate}`;
        
        content += `
          <div style="position: absolute; top: ${y}mm; left: ${x}mm; width: ${width}mm; 
                      font-size: ${fontSize}px; font-family: ${fontFamily}; line-height: ${lineHeight}px; 
                      font-weight: bold; color: black; margin: 0; padding: 0;">
            ${subjectText}
          </div>
        `;
      }
    }
    
        // Body mit geclipptem Content (Layout-basiert + BEIBEHALTUNG DER INLINE-STYLES!)
        if (pdfData.layout?.areas && clippedBodyContent) {
          const bodyArea = pdfData.layout.areas.find(area => area.type === 'body' && area.page === 'first');
          if (bodyArea) {
            const x = bodyArea.x * 0.5;
            const y = bodyArea.y * 0.5;
            const width = bodyArea.width * 0.5;
            
            // VERWENDE NUR BASIS-STYLES - INLINE-STYLES BLEIBEN ERHALTEN!
            const previewStyles = window.exactPreviewStyles;
            const baseFontSize = bodyArea.fontSize || 11; // NUR als Basis - Inline-Styles ├╝berschreiben das!
            const fontFamily = previewStyles ? previewStyles.fontFamily : 'Helvetica, Arial, sans-serif';
            const lineHeight = previewStyles ? previewStyles.lineHeight : '13.2px';
            const letterSpacing = previewStyles ? previewStyles.letterSpacing : '0.15008px';
            const wordSpacing = previewStyles ? previewStyles.wordSpacing : '0px';
            const textRendering = previewStyles ? previewStyles.textRendering : 'optimizeLegibility';
            const wordWrap = previewStyles ? previewStyles.wordWrap : 'break-word';
            const wordBreak = previewStyles ? previewStyles.wordBreak : 'normal';
            const overflowWrap = previewStyles ? previewStyles.overflowWrap : 'break-word';
            const whiteSpace = previewStyles ? previewStyles.whiteSpace : 'normal';
            
            console.log(`­ƒÄ» ERSTE SEITE - BASIS-FONT-SIZE: ${baseFontSize}px (Inline-Styles bleiben erhalten!)`);
            
            content += `
              <div style="position: absolute; top: ${y}mm; left: ${x}mm; width: ${width}mm; 
                          font-size: ${baseFontSize}px; font-family: ${fontFamily}; line-height: ${lineHeight}; 
                          letter-spacing: ${letterSpacing}; word-spacing: ${wordSpacing};
                          text-rendering: ${textRendering}; word-wrap: ${wordWrap}; word-break: ${wordBreak};
                          overflow-wrap: ${overflowWrap}; white-space: ${whiteSpace};
                          color: black; overflow: hidden; margin: 0; padding: 0;">
                ${clippedBodyContent}
              </div>
            `;
            
            console.log(`­ƒôÅ BREITEN-DEBUG - Body-Area: width=${width}mm (Layout: ${bodyArea.width}pts * 0.5 = ${bodyArea.width * 0.5}mm)`);
            console.log(`­ƒôÅ BREITEN-DEBUG - Position: x=${x}mm, y=${y}mm, baseFontSize=${baseFontSize}px`);
            console.log(`­ƒÄ» BASIS-STYLES verwendet (Inline-Styles ├╝berschreiben):`, {baseFontSize, fontFamily, lineHeight, letterSpacing, wordSpacing});
          }
        }
    
    return content;
  };
  
  // Hilfsfunktion: Generiert Folgeseiten mit geclipptem Content
  const generateFollowingPageWithClippedContent = (clippedContent) => {
    let content = '';
    
    // Wasserzeichen f├╝r nicht freigegebene Reports
    if (showWatermark) {
      content += `
        <div style="position: absolute; top: 0; left: 0; right: 0; bottom: 0; z-index: 999; 
                    display: flex; justify-content: center; align-items: center; pointer-events: none;">
          <div style="font-size: 80px; font-weight: bold; color: rgba(255, 0, 0, 0.15); 
                      transform: rotate(-45deg); text-transform: uppercase; white-space: nowrap; 
                      user-select: none; letter-spacing: 0.1em;">
            ${watermarkText}
          </div>
        </div>
      `;
    }
    
        // Body area f├╝r Folgeseiten (BASIS-STYLES + BEIBEHALTUNG DER INLINE-STYLES!)
        if (pdfData.layout?.areas && clippedContent) {
          const bodyArea = pdfData.layout.areas.find(area => area.type === 'body' && area.page === 'following');
          if (bodyArea) {
            const x = bodyArea.x * 0.5;
            const y = bodyArea.y * 0.5;
            const width = bodyArea.width * 0.5;
            
            // VERWENDE NUR BASIS-STYLES - INLINE-STYLES BLEIBEN ERHALTEN!
            const previewStyles = window.exactPreviewStyles;
            const baseFontSize = bodyArea.fontSize || 11; // NUR als Basis - Inline-Styles ├╝berschreiben das!
            const fontFamily = previewStyles ? previewStyles.fontFamily : 'Helvetica, Arial, sans-serif';
            const lineHeight = previewStyles ? previewStyles.lineHeight : '13.2px';
            const letterSpacing = previewStyles ? previewStyles.letterSpacing : '0.15008px';
            const wordSpacing = previewStyles ? previewStyles.wordSpacing : '0px';
            const textRendering = previewStyles ? previewStyles.textRendering : 'optimizeLegibility';
            const wordWrap = previewStyles ? previewStyles.wordWrap : 'break-word';
            const wordBreak = previewStyles ? previewStyles.wordBreak : 'normal';
            const overflowWrap = previewStyles ? previewStyles.overflowWrap : 'break-word';
            const whiteSpace = previewStyles ? previewStyles.whiteSpace : 'normal';
            
            console.log(`­ƒÄ» FOLGESEITE - BASIS-FONT-SIZE: ${baseFontSize}px (Inline-Styles bleiben erhalten!)`);
            
            content += `
              <div style="position: absolute; top: ${y}mm; left: ${x}mm; width: ${width}mm; 
                          font-size: ${baseFontSize}px; font-family: ${fontFamily}; line-height: ${lineHeight}; 
                          letter-spacing: ${letterSpacing}; word-spacing: ${wordSpacing};
                          text-rendering: ${textRendering}; word-wrap: ${wordWrap}; word-break: ${wordBreak};
                          overflow-wrap: ${overflowWrap}; white-space: ${whiteSpace};
                          color: black; overflow: hidden; margin: 0; padding: 0;">
                ${clippedContent}
              </div>
            `;
          }
        }
    
    return content;
  };

  // NEUER ANSATZ: Komplettes HTML + CSS an Backend senden
  const handleDownloadNew = async () => {
    if (!pdfData) return;
    
    try {
      setLoading(true);
      
      const reportId = pdfData.report?.id || pdfData.report_id;
      if (!reportId) {
        throw new Error('Report ID nicht gefunden');
      }
      
      console.log(`­ƒôÑ NEUER ANSATZ: Extrahiere komplettes HTML f├╝r Report ${reportId}`);
      
      // Finde das PDF-Preview-Container
      const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
      if (!dialogContent) {
        throw new Error('Dialog content not found');
      }
      
      // Extrahiere das komplette HTML aller Seiten
      const paperElements = dialogContent.querySelectorAll('.MuiPaper-root');
      console.log(`­ƒôä Gefundene Seiten: ${paperElements.length}`);
      
      if (paperElements.length === 0) {
        throw new Error('Keine PDF-Seiten gefunden');
      }
      
      // Sammle HTML aller Seiten mit korrekten Styles
      let fullHTML = '';
      
      // Basis-CSS f├╝r PDF-Rendering
      const baseCSS = `
        @page {
          size: A4;
          margin: 0;
        }
        
        .pdf-page {
          width: 210mm;
          height: 297mm;
          position: relative;
          background: white;
          page-break-after: always;
          margin: 0;
          padding: 0;
          overflow: hidden;
          box-sizing: border-box;
        }
        
        /* Preserve all positioning and styles */
        * {
          box-sizing: border-box;
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
      `;
      
      for (let pageIdx = 0; pageIdx < paperElements.length; pageIdx++) {
        const paperElement = paperElements[pageIdx];
        
        // Erstelle Seiten-Container mit exaktem HTML
        const pageHTML = `
          <div class="pdf-page">
            ${paperElement.innerHTML}
          </div>
        `;
        
        fullHTML += pageHTML;
      }
      
      // Sammle alle verwendeten CSS-Klassen und deren Styles
      const usedStyles = new Set();
      const allElements = dialogContent.querySelectorAll('*');
      
      allElements.forEach(element => {
        const computedStyle = window.getComputedStyle(element);
        const classList = Array.from(element.classList);
        
        classList.forEach(className => {
          if (className.startsWith('Mui')) {
            const cssRule = `
              .${className} {
                position: ${computedStyle.position};
                top: ${computedStyle.top};
                left: ${computedStyle.left};
                width: ${computedStyle.width};
                height: ${computedStyle.height};
                font-family: ${computedStyle.fontFamily};
                font-size: ${computedStyle.fontSize};
                font-weight: ${computedStyle.fontWeight};
                line-height: ${computedStyle.lineHeight};
                color: ${computedStyle.color};
                background: ${computedStyle.background};
                background-image: ${computedStyle.backgroundImage};
                background-size: ${computedStyle.backgroundSize};
                background-position: ${computedStyle.backgroundPosition};
                background-repeat: ${computedStyle.backgroundRepeat};
                z-index: ${computedStyle.zIndex};
                overflow: ${computedStyle.overflow};
                margin: ${computedStyle.margin};
                padding: ${computedStyle.padding};
                border: ${computedStyle.border};
                display: ${computedStyle.display};
                text-align: ${computedStyle.textAlign};
                white-space: ${computedStyle.whiteSpace};
                word-wrap: ${computedStyle.wordWrap};
              }
            `;
            usedStyles.add(cssRule);
          }
        });
      });
      
      const cssContent = baseCSS + Array.from(usedStyles).join('\n');
      
      console.log(`­ƒôï HTML extrahiert: ${fullHTML.length} Zeichen`);
      console.log(`­ƒÄ¿ CSS extrahiert: ${cssContent.length} Zeichen`);
      
      // Token aus localStorage holen
      const token = localStorage.getItem('token');
      
      // Backend-Endpoint aufrufen (POST mit HTML + CSS)
      const backendUrl = localStorage.getItem('backendUrl') || 'http://127.0.0.1:25000';
      const url = `${backendUrl}/reports/${reportId}/download-pdf-weasyprint${token ? `?token=${token}` : ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          html: fullHTML,
          css: cssContent,
          background_images: pdfData.background_images
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`PDF-Download fehlgeschlagen: ${response.statusText} - ${errorText}`);
      }
      
      // PDF als Blob herunterladen
      const blob = await response.blob();
      
      // Download triggern
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Dateiname generieren
      const patientName = pdfData.report?.new_report?.new_exam?.patient_name || 'Patient';
      const patientNameSafe = patientName.replace(/[^a-zA-Z0-9_-]/g, '_');
      const filename = `Befund_${patientNameSafe}_${reportId}.pdf`;
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      console.log(`Ô£à PDF erfolgreich heruntergeladen: ${filename}`);
      
    } catch (error) {
      console.error('ÔØî Fehler beim PDF-Download:', error);
      showInfo(t('pdfPreview.downloadError', 'Fehler beim PDF-Download: ') + error.message);
    } finally {
      setLoading(false);
    }
  };

  // Handle PDF Download - Extrahiert strukturierte Bereiche und sendet ans Backend
  const handleDownload = async () => {
    if (!pdfData) return;
    
    try {
      setLoading(true);
      
      const reportId = pdfData.report?.id || pdfData.report_id;
      if (!reportId) {
        throw new Error('Report ID nicht gefunden');
      }
      
      console.log(`­ƒôÑ Erstelle strukturiertes PDF f├╝r Report ${reportId}...`);
      
      // Extrahiere ALLE gerenderten Bereiche aus dem DOM (so wie sie angezeigt werden)
      const dialogContent = document.querySelector('[role="dialog"] .MuiDialogContent-root');
      if (!dialogContent) {
        throw new Error('Dialog content not found');
      }
      
      const paperElements = dialogContent.querySelectorAll('.MuiPaper-root');
      console.log(`­ƒôä Gefundene Seiten: ${paperElements.length}`);
      
      const pages = [];
      
      for (let pageIdx = 0; pageIdx < paperElements.length; pageIdx++) {
        const paperElement = paperElements[pageIdx];
        const pageAreas = [];
        
        // Finde NUR die Content-Bereiche (letterhead, patientinfo, subject, body)
        // Nicht Container/Wrapper mit position:absolute
        const boxElements = paperElement.querySelectorAll('.MuiBox-root');
        
        console.log(`  ­ƒôï Seite ${pageIdx + 1}: ${boxElements.length} MuiBox-Elemente gefunden (wird gefiltert)`);
        
        for (let boxIdx = 0; boxIdx < boxElements.length; boxIdx++) {
          const box = boxElements[boxIdx];
          
          // Hole computed styles
          const computedStyle = window.getComputedStyle(box);
          
          // Pr├╝fe ob position: absolute
          if (computedStyle.position !== 'absolute') {
            continue;
          }
          
          // Skip Wasserzeichen (z-index 999)
          const zIndex = parseInt(computedStyle.zIndex);
          if (zIndex === 999 || zIndex >= 900) {
            console.log(`    ÔÅ¡´©Å ├£berspringe Wasserzeichen (z-index: ${zIndex})`);
            continue;
          }
          
          // Extrahiere Position aus computed styles (bereits in px)
          const top = parseFloat(computedStyle.top);
          const left = parseFloat(computedStyle.left);
          const width = parseFloat(computedStyle.width);
          const height = parseFloat(computedStyle.height);
          
          if (isNaN(top) || isNaN(left) || isNaN(width)) {
            console.warn(`    ÔÜá´©Å Box ${boxIdx} hat keine g├╝ltigen Koordinaten`);
            continue;
          }
          
          // Skip volle Seitengr├Â├ƒe (Container-Elemente)
          // A4 ist ca. 794 x 1123 px bei 96dpi
          if (width > 750 && height > 1000) {
            console.log(`    ÔÅ¡´©Å ├£berspringe Container (Gr├Â├ƒe: ${width.toFixed(0)}x${height.toFixed(0)}px)`);
            continue;
          }
          
          // Konvertiere px zu mm (konsistent mit Preview-Renderung)
          // Preview verwendet: mm zu px = * 3.7795275591
          // Download verwendet: px zu mm = / 3.7795275591
          const PX_TO_MM = 1 / 3.7795275591;
          const x = left * PX_TO_MM;
          const y = top * PX_TO_MM;
          const widthMm = width * PX_TO_MM;
          const heightMm = height * PX_TO_MM;
          
          // Extrahiere Content - WICHTIG: Pr├╝fe ob HTML-Formatierung vorhanden ist
          const innerHTML = box.innerHTML;
          const textContent = box.textContent || '';
          
          // Skip wenn kein echter Text-Content
          if (!textContent || textContent.trim() === '') {
            console.log(`    ÔÅ¡´©Å ├£berspringe leeres Element`);
            continue;
          }
          
          // Bestimme ob HTML oder Plain Text
          // Wenn innerHTML <strong>, <span style> etc. enth├ñlt ÔåÆ HTML
          // Wenn nur <p> oder <br> ÔåÆ Plain Text mit Zeilenumbr├╝chen
          const hasFormatting = /<strong|<span[^>]*style|<em|<b>|<i>/.test(innerHTML);
          
          let content;
          if (hasFormatting) {
            // HTML mit Formatierung (Fett, Farben, etc.) ÔåÆ sende HTML
            content = innerHTML;
          } else {
            // Kein HTML-Styling ÔåÆ sende Plain Text mit Zeilenumbr├╝chen
            // Ersetze <br>, </p> durch \n
            const div = document.createElement('div');
            div.innerHTML = innerHTML;
            
            // Gehe durch alle Kinder und sammle Text mit \n
            const lines = [];
            const processNode = (node) => {
              if (node.nodeType === Node.TEXT_NODE) {
                const text = node.textContent.trim();
                if (text) lines.push(text);
              } else if (node.nodeType === Node.ELEMENT_NODE) {
                // Verarbeite Kinder
                Array.from(node.childNodes).forEach(processNode);
                // Nach Block-Elementen Zeilenumbruch
                if (['P', 'DIV', 'BR'].includes(node.tagName)) {
                  // Marker f├╝r Zeilenumbruch
                  if (lines[lines.length - 1] !== '\n') {
                    lines.push('\n');
                  }
                }
              }
            };
            
            Array.from(div.childNodes).forEach(processNode);
            content = lines.join('').replace(/\n+/g, '\n').trim();
          }
          
          console.log(`    ­ƒôä Content type: ${hasFormatting ? 'HTML (formatted)' : 'Plain text'}, length: ${content.length}`);
          
          // Extrahiere Basis-Font-Info
          const fontSize = parseInt(computedStyle.fontSize);
          const fontFamily = computedStyle.fontFamily.replace(/['"]/g, '');
          const fontWeight = computedStyle.fontWeight;
          const color = computedStyle.color;
          
          // RGB zu Hex konvertieren
          let hexColor = '#000000';
          const rgbMatch = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
          if (rgbMatch) {
            const r = parseInt(rgbMatch[1]);
            const g = parseInt(rgbMatch[2]);
            const b = parseInt(rgbMatch[3]);
            hexColor = `#${r.toString(16).padStart(2, '0')}${g.toString(16).padStart(2, '0')}${b.toString(16).padStart(2, '0')}`;
          }
          
          pageAreas.push({
            type: `area_${boxIdx}`,
            x: x,
            y: y,
            width: widthMm,
            height: heightMm,
            content: content,
            fontSize: fontSize,
            fontFamily: fontFamily,
            fontWeight: fontWeight,
            color: hexColor,
            bold: fontWeight === 'bold' || parseInt(fontWeight) >= 700
          });
          
          console.log(`    Ô£à Box ${boxIdx}: x=${x.toFixed(1)}mm, y=${y.toFixed(1)}mm, w=${widthMm.toFixed(1)}mm, fontSize=${fontSize}px`);
        }
        
        pages.push({ areas: pageAreas });
        console.log(`  Ô£à Seite ${pageIdx + 1}: ${pageAreas.length} Bereiche extrahiert`);
      }
      
      console.log(`­ƒôï Gesamt extrahiert: ${pages.length} Seiten mit Bereichen`);
      
      // Token aus localStorage holen
      const token = localStorage.getItem('token');
      
      // Backend-Endpoint aufrufen (POST mit strukturierten Bereichen)
      const backendUrl = localStorage.getItem('backendUrl') || 'http://127.0.0.1:25000';
      const url = `${backendUrl}/reports/${reportId}/download-pdf-from-html${token ? `?token=${token}` : ''}`;
      
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          pages: pages,
          background_images: pdfData.background_images
        })
      });
      
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`PDF-Download fehlgeschlagen: ${response.statusText} - ${errorText}`);
      }
      
      // PDF als Blob herunterladen
      const blob = await response.blob();
      
      // Download triggern
      const downloadUrl = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = downloadUrl;
      
      // Dateiname aus Response-Header oder generieren
      const contentDisposition = response.headers.get('content-disposition');
      let filename = 'Befund.pdf';
      
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="?(.+)"?/i);
        if (filenameMatch) {
          filename = filenameMatch[1];
        }
      } else {
        // Fallback: Dateiname generieren
        const patientName = pdfData.report?.new_report?.new_exam?.patient_name || 'Patient';
        filename = `Befund_${patientName.replace(/[^a-zA-Z0-9]/g, '_')}_${reportId}.pdf`;
      }
      
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(downloadUrl);
      
      console.log(`Ô£à PDF erfolgreich heruntergeladen: ${filename}`);
      
    } catch (error) {
      console.error('ÔØî Fehler beim PDF-Download:', error);
      setError(`Fehler beim Erstellen des PDFs: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  // Handle Fullscreen Toggle
  const handleFullscreen = () => {
    setIsFullscreen(!isFullscreen);
  };

  // Handle Dialog Close
  const handleClose = () => {
    setIsFullscreen(false);
    setError(null);
    onClose();
  };

  // Auto-Download Effect - startet automatisch Download wenn Flag gesetzt ist
  useEffect(() => {
    if (pdfData?.autoDownload && open) {
      console.log('­ƒôÑ AUTO-DOWNLOAD: Flag erkannt, starte Download automatisch...');
      
      // Warte kurz, damit das Dialog vollst├ñndig gerendert ist
      const timer = setTimeout(async () => {
        try {
          await handleDownloadBrowserNative();
          console.log('Ô£à AUTO-DOWNLOAD: Download erfolgreich gestartet, schlie├ƒe Dialog...');
          
          // Schlie├ƒe Dialog nach kurzem Delay
          setTimeout(() => {
            onClose();
          }, 1000);
        } catch (error) {
          console.error('ÔØî AUTO-DOWNLOAD: Fehler beim automatischen Download:', error);
        }
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [pdfData, open, handleDownloadBrowserNative, onClose]);

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={isPopupWindow ? true : isFullscreen}
      maxWidth={isPopupWindow ? false : (isFullscreen ? false : "lg")}
      fullWidth={!isPopupWindow}
      PaperProps={{
        sx: isPopupWindow ? {
          // Popup-Fenster: An A4-Seitenbreite angepasst
          margin: 0,
          maxWidth: '100vw',
          width: '100vw',
          height: '100vh',
          maxHeight: '100vh',
          borderRadius: 0,
          opacity: pdfData?.autoDownload ? 0 : 1,
          pointerEvents: pdfData?.autoDownload ? 'none' : 'auto',
          display: 'flex',
          flexDirection: 'column'
        } : {
          // Normaler Dialog
          height: isFullscreen ? '100vh' : '90vh',
          maxHeight: isFullscreen ? 'none' : '90vh',
          opacity: pdfData?.autoDownload ? 0 : 1,
          pointerEvents: pdfData?.autoDownload ? 'none' : 'auto'
        }
      }}
    >
      {/* Loading Backdrop */}
      <Backdrop
        sx={{ 
          color: 'var(--rd-primary-contrast)', 
          zIndex: (theme) => theme.zIndex.modal + 1,
          position: 'absolute'
        }}
        open={loading}
      >
        <Box display="flex" flexDirection="column" alignItems="center">
          <CircularProgress color="inherit" />
          <Typography variant="body2" sx={{ mt: 2 }}>
            {t('pdfPreview.rendering', 'Report wird erstellt...')}
          </Typography>
        </Box>
      </Backdrop>

      {/* Dialog Header */}
      <DialogTitle sx={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        borderBottom: 1,
        borderColor: 'divider'
      }}>
        <Typography variant="h6" component="span">
          {t('pdfPreview.title', reportTitle)}
        </Typography>
        <Box>
          <IconButton
              onClick={handleDownloadBrowserNative}
            disabled={!pdfData || loading}
            title={t('pdfPreview.download', 'Herunterladen')}
            sx={{ display: 'none' }} // Ausgeblendet, Funktion bleibt erhalten
          >
            <DownloadIcon />
          </IconButton>
          <IconButton
            onClick={handleFullscreen}
            title={t('pdfPreview.fullscreen', 'Vollbild')}
            sx={{ display: 'none' }} // Ausgeblendet, Funktion bleibt erhalten
          >
            <FullscreenIcon />
          </IconButton>
          <IconButton
            onClick={handleClose}
            title={t('pdfPreview.close', 'Schlie├ƒen')}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      {/* ===== BACKUP: ALTES DIALOG CONTENT (iframe-basiert) =====
      <DialogContent sx={{ 
        p: 2, 
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#f5f5f5',
        transform: `scale(${dialogZoom})`,
        transformOrigin: 'center top',
        transition: 'transform 0.2s ease'
      }}>
        {error ? (
          <Box p={3}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('pdfPreview.error', 'Fehler beim Laden des PDFs')}: {error}
            </Alert>
            <Button 
              variant="contained" 
              onClick={() => {
                window.location.reload();
              }}
            >
              {t('pdfPreview.retry', 'Erneut versuchen')}
            </Button>
          </Box>
        ) : (
          <Box
            ref={pdfContainerRef}
            sx={{
              width: '100%',
              minHeight: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: pdfData ? 'flex-start' : 'center',
              paddingTop: pdfData ? 2 : 0
            }}
          >
            {!pdfData && !loading && (
              <Typography variant="body1" color="text.secondary">
                {t('pdfPreview.noPdf', 'Kein PDF verf├╝gbar')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      ===== ENDE BACKUP ===== */}

      {/* ===== OPTION A: HTML-PAPER MIT <OBJECT> BACKGROUND ===== */}
      <DialogContent sx={{ 
        p: 0, 
        position: 'relative',
        height: '100%',
        overflow: 'auto',
        backgroundColor: '#e0e0e0', // Dunkler Hintergrund f├╝r besseren Kontrast
        display: 'flex',
        justifyContent: 'center'
      }}>
        {error ? (
          <Box p={3}>
            <Alert severity="error" sx={{ mb: 2 }}>
              {t('pdfPreview.error', 'Fehler beim Laden des PDFs')}: {error}
            </Alert>
            <Button 
              variant="contained" 
              onClick={() => {
                window.location.reload();
              }}
            >
              {t('pdfPreview.retry', 'Erneut versuchen')}
            </Button>
          </Box>
        ) : (
          <Box
            sx={{
              width: 'fit-content', // Nur so breit wie die Seiten
              minHeight: '100%',
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'flex-start',
              paddingTop: pdfData ? 3 : 0,
              paddingBottom: pdfData ? 3 : 0,
              margin: '0 auto' // Horizontal zentrieren
            }}
          >
            {!pdfData && !loading ? (
              <Typography variant="body1" color="text.secondary">
                {t('pdfPreview.noPdf', 'Kein PDF verf├╝gbar')}
              </Typography>
            ) : pdfData && (
              <Box 
                sx={{ 
                  transform: `scale(${paperZoom})`, 
                  transformOrigin: 'center top', 
                  transition: 'transform 0.2s ease',
                  display: 'flex',
                  flexDirection: 'column', // ­ƒôä WICHTIG: Vertikales Layout f├╝r Seiten untereinander!
                  gap: '20px' // Abstand zwischen den Seiten
                }}
              >
                {/* ERSTE SEITE (Page 1) */}
              <Paper
                sx={{
                  width: '210mm',  // A4 Breite
                  minHeight: '297mm', // A4 H├Âhe
                  maxWidth: '100%',
                  position: 'relative',
                  backgroundColor: 'white',
                  boxShadow: '3px 3px 12px rgba(0,0,0,0.15), 1px 1px 3px rgba(0,0,0,0.1)',
                  border: 'none',
                  outline: 'none',
                  borderRadius: 0,
                    overflow: 'hidden'
                  }}
                >
                {/* Wasserzeichen f├╝r nicht freigegebene Reports */}
                {showWatermark && <Watermark text={watermarkText} />}

                {/* PDF-Hintergrund als PNG (Backend-konvertiert) */}
                {pdfData.background_images?.firstPage && (() => {
                  const originalUrl = pdfData.background_images.firstPage.url;
                  const pngUrl = originalUrl.replace('/background/', '/background-png/');
                  
                  return (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: '100%',
                      height: '100%',
                      zIndex: 1, // Im Hintergrund
                      // PDF-URL zu PNG-URL konvertieren (neuer Endpoint)
                      backgroundImage: `url(${pngUrl})`,
                      backgroundSize: 'cover', // Cover statt contain - f├╝llt komplette Box
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'center center', // Zentriert
                      opacity: 1.0 // Vollst├ñndig sichtbar
                    }}
                  >
                    {/* PNG l├ñdt erfolgreich - Background sollte sichtbar sein */}
                  </Box>
                  );
                })()}

                {/* HTML-Content Layer (├╝ber PDF) */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    zIndex: 2,
                    pointerEvents: 'none', // Transparent f├╝r Background-Sichtbarkeit
                    color: 'black',
                    fontFamily: '"Times New Roman", serif'
                  }}
                >
                  {/* Briefkopf-Rendering */}
                  {(() => {
                    if (!pdfData.layout?.areas) return null;
                    if (!pdfData.address) return null;
                    
                    // Finde letterhead area
                    const letterheadArea = pdfData.layout.areas.find(area => area.type === 'letterhead');
                    if (!letterheadArea) {
                      return null;
                    }

                    // Konvertiere Koordinaten: points zu mm
                    const x = letterheadArea.x * 0.5;
                    const y = letterheadArea.y * 0.5;
                    const width = letterheadArea.width * 0.5;
                    const fontSize = letterheadArea.fontSize || 12;
                    const fontFamily = letterheadArea.fontFamily || '"Segoe UI"';
                    const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px

                    // Erstelle Briefkopf-Zeilen
                    const addressLines = [];
                    const addr = pdfData.address;

                    // 1. Zeile: Organization (nur wenn vorhanden)
                    if (addr.organization && addr.organization.trim()) {
                      addressLines.push(addr.organization.trim());
                    }

                    // 2. Zeile: Salutation + Title + Vorname + Nachname
                    const nameParts = [
                      addr.salutation,
                      addr.title,
                      addr.first_name,
                      addr.last_name
                    ].filter(part => part && part.trim()).map(part => part.trim());

                    if (nameParts.length > 0) {
                      addressLines.push(nameParts.join(' '));
                    }

                    // 3. Zeile: Stra├ƒe
                    if (addr.street && addr.street.trim()) {
                      addressLines.push(addr.street.trim());
                    }

                    // 4. Zeile: PLZ + Stadt
                    const cityParts = [addr.postal_code, addr.city]
                      .filter(part => part && part.trim())
                      .map(part => part.trim());
                    
                    if (cityParts.length > 0) {
                      addressLines.push(cityParts.join(' '));
                    }

                    // 5. Zeile: Land
                    if (addr.country && addr.country.trim()) {
                      addressLines.push(addr.country.trim());
                    }

                    return (
                      <Box
                        sx={{
                          position: 'absolute',
                          top: `${y}mm`,
                          left: `${x}mm`,
                          width: `${width}mm`,
                          fontSize: `${fontSize}px`,
                          fontFamily: fontFamily,
                          lineHeight: `${lineHeight}px`,
                          pointerEvents: 'auto'
                        }}
                      >
                        {addressLines.map((line, index) => (
                          <Typography
                            key={index}
                            sx={{
                              fontSize: `${fontSize}px`,
                              lineHeight: `${lineHeight}px`,
                              margin: 0,
                              padding: 0,
                              fontFamily: fontFamily,
                              color: 'black'
                            }}
                          >
                            {line}
                          </Typography>
                        ))}
                      </Box>
                    );
                  })()}

                  {/* Patienteninformation-Rendering */}
                  {(() => {
                    if (!pdfData.layout?.areas) return null;
                    
                    const patientinfoArea = pdfData.layout.areas.find(area => area.type === 'patientinfo');
                    if (!patientinfoArea) return null;

                    // Konvertiere Koordinaten: points zu mm
                    const x = patientinfoArea.x * 0.5;
                    const y = patientinfoArea.y * 0.5;
                    const width = patientinfoArea.width * 0.5;
                    const fontSize = patientinfoArea.fontSize || 11;
                    const fontFamily = patientinfoArea.fontFamily || '"Segoe UI"';
                    const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px

                    // Patient-Daten aus pdfData
                    const patientData = pdfData.report?.new_report?.new_exam;
                    if (!patientData) return null;

                    // Geburtsdatum formatieren (DD.MM.YYYY)
                    let formattedBirthDate = patientData.birth_date || '[Geburtsdatum]';
                    if (patientData.birth_date) {
                      try {
                        const birthDate = new Date(patientData.birth_date);
                        formattedBirthDate = birthDate.toLocaleDateString('de-DE');
                      } catch (e) {
                        console.warn('Fehler beim Formatieren des Geburtsdatums:', patientData.birth_date, e);
                      }
                    }

                    // Geschlechtsspezifische Anrede basierend auf new_exams.sex
                    const sex = patientData.sex;
                    let genderSpecificText;
                    if (sex === 'M') {
                      genderSpecificText = t('patient.male');
                    } else if (sex === 'F') {
                      genderSpecificText = t('patient.female');
                    } else if (sex === 'O') {
                      genderSpecificText = t('patient.other');
                    } else {
                      genderSpecificText = t('patient.other');
                    }

                    // Format: "[Geschlechtsspezifisch]: [Name, Vorname], *[Geburtsdatum], Patienten-ID [patient_id]"
                    const patientText = `${genderSpecificText}: ${patientData.patient_name || '[Name, Vorname]'}, *${formattedBirthDate}, Patienten-ID ${patientData.patient_id || '[patient_id]'}`;

                    return (
                      <Box
                        key="patientinfo"
                        sx={{
                          position: 'absolute',
                          top: `${y}mm`,
                          left: `${x}mm`,
                          width: `${width}mm`,
                          fontSize: `${fontSize}px`,
                          fontFamily: fontFamily,
                          lineHeight: `${lineHeight}px`,
                          pointerEvents: 'auto'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: `${fontSize}px`,
                            fontFamily: fontFamily,
                            lineHeight: `${lineHeight}px`,
                            margin: 0,
                            padding: 0,
                            color: 'black',
                            fontWeight: 'bold'
                          }}
                        >
                          {patientText}
                        </Typography>
                      </Box>
                    );
                  })()}

                  {/* Betreff-Rendering */}
                  {(() => {
                    if (!pdfData.layout?.areas) return null;
                    
                    const subjectArea = pdfData.layout.areas.find(area => area.type === 'subject');
                    if (!subjectArea) return null;

                    // Konvertiere Koordinaten: points zu mm
                    const x = subjectArea.x * 0.5;
                    const y = subjectArea.y * 0.5;
                    const width = subjectArea.width * 0.5;
                    const fontSize = subjectArea.fontSize || 12;
                    const fontFamily = subjectArea.fontFamily || '"Segoe UI"';
                    const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px

                    // Betreff mit Platzhaltern und Datumsformatierung
                    const examData = pdfData.report?.new_report?.new_exam;
                    const studyDate = examData?.studydate || examData?.studyDate || examData?.study_date;
                    const modality = examData?.modality || 'CT';
                    
                    // Datum formatieren (DD.MM.YYYY)
                    let formattedDate = 'Datum';
                    if (studyDate) {
                      try {
                        const date = new Date(studyDate);
                        if (!isNaN(date.getTime())) {
                          formattedDate = date.toLocaleDateString('de-DE');
                        } else {
                          formattedDate = studyDate.toString();
                        }
                      } catch (e) {
                        console.warn('Fehler beim Formatieren des Datums:', studyDate, e);
                        formattedDate = studyDate.toString();
                      }
                    }
                    
                    const subjectText = `${modality}-Untersuchung vom ${formattedDate}`;

                    return (
                      <Box
                        key="subject"
                        sx={{
                          position: 'absolute',
                          top: `${y}mm`,
                          left: `${x}mm`,
                          width: `${width}mm`,
                          fontSize: `${fontSize}px`,
                          fontFamily: fontFamily,
                          lineHeight: `${lineHeight}px`,
                          pointerEvents: 'auto'
                        }}
                      >
                        <Typography
                          sx={{
                            fontSize: `${fontSize}px`,
                            fontFamily: fontFamily,
                            lineHeight: `${lineHeight}px`,
                            margin: 0,
                            padding: 0,
                            color: 'black',
                            fontWeight: 'bold'
                          }}
                        >
                          {subjectText}
                        </Typography>
                      </Box>
                    );
                  })()}


                  {/* Body/Befundinhalt-Rendering */}
                  {(() => {
                    if (!pdfData.layout?.areas) return null;
                    
                    const bodyArea = pdfData.layout.areas.find(area => area.type === 'body' && area.page === 'first');
                    if (!bodyArea) return null;

                    // Konvertiere Koordinaten: points zu mm
                    const x = bodyArea.x * 0.5;
                    const y = bodyArea.y * 0.5;
                    const width = bodyArea.width * 0.5;
                    const height = bodyArea.height * 0.5;
                    const fontSize = bodyArea.fontSize || 11;
                    const fontFamily = bodyArea.fontFamily || '"Segoe UI"';
                    const lineHeight = 13.2; // EXAKT wie in Preview: 13.2px

                    // Gru├ƒformel f├╝r Body-Beginn generieren
                    let greetingText = '';
                    if (pdfData.layout?.greeting_templates && pdfData.address) {
                      const physicianGender = pdfData.address.gender;
                      let templateKey = 'none';
                      
                      if (physicianGender === 'm├ñnnlich') templateKey = 'male';
                      else if (physicianGender === 'weiblich') templateKey = 'female';
                      else if (physicianGender === 'divers') templateKey = 'diverse';
                      
                      let greetingTemplate = pdfData.layout.greeting_templates[templateKey] || pdfData.layout.greeting_templates['none'];
                      
                      if (greetingTemplate) {
                        const examData = pdfData.report?.new_report?.new_exam;
                        const studyDate = examData?.studydate || examData?.studyDate || examData?.study_date;
                        
                        let formattedStudyDate = 'Datum';
                        if (studyDate) {
                          try {
                            const date = new Date(studyDate);
                            if (!isNaN(date.getTime())) {
                              formattedStudyDate = date.toLocaleDateString('de-DE');
                            }
                          } catch (e) {
                            formattedStudyDate = studyDate.toString();
                          }
                        }
                        
                        greetingText = greetingTemplate
                          .replace('[referring_physician_title]', pdfData.address.title || '')
                          .replace('[referring_physician_last_name]', pdfData.address.last_name || '')
                          .replace('[study_date]', formattedStudyDate);
                      }
                    }

                    // Strukturierter Report-Content basierend auf Template
                    let contentParts = [];
                    
                    console.log('🔍 DEBUG PDFPreviewDialogStructured - pdfData:', pdfData);
                    console.log('🔍 DEBUG PDFPreviewDialogStructured - pdfData.report:', pdfData.report);
                    console.log('🔍 DEBUG PDFPreviewDialogStructured - pdfData.report?.report:', pdfData.report?.report);
                    console.log('🔍 DEBUG PDFPreviewDialogStructured - template_data:', pdfData.report?.report?.template_data);
                    
                    if (pdfData.report?.report?.template_data) {
                      const templateData = pdfData.report.report.template_data;
                      const examData = pdfData.report?.new_report?.new_exam;
                      
                      console.log('🔍 DEBUG PDFPreviewDialogStructured - templateData:', templateData);
                      console.log('🔍 DEBUG PDFPreviewDialogStructured - examData:', examData);
                      
                      // 🔥 DEBUG: Schaue dir jeden templateData-Item genau an
                      templateData.forEach((item, index) => {
                        console.log(`🔍 DEBUG Item ${index}:`, item);
                        console.log(`🔍 DEBUG Item ${index} - item_type:`, item.item_type);
                        console.log(`🔍 DEBUG Item ${index} - findings:`, item.findings);
                        console.log(`🔍 DEBUG Item ${index} - findings length:`, item.findings ? item.findings.length : 'NO FINDINGS');
                      });
                      
                      templateData.forEach(item => {
                        if (item.item_type === 'field' || item.type === 'field' || item.type === 'freetext' || item.type === 'database') {
                          // 📝 NORMALE FELDER (wie Anamnese mit database_field)
                          let fieldContent = '';
                          
                          if (item.database_field) {
                            // Database-Feld verarbeiten
                            switch (item.database_field) {
                              case 'clinical_info':
                                fieldContent = examData?.clinical_info || '[Klinische Informationen nicht verfügbar]';
                                break;
                              case 'patient_id':
                                fieldContent = examData?.patient_id || '[Patienten-ID nicht verfügbar]';
                                break;
                              case 'patient_name':
                                fieldContent = examData?.patient_name || '[Patientenname nicht verfügbar]';
                                break;
                              case 'birth_date':
                                if (examData?.birth_date) {
                                  try {
                                    const date = new Date(examData.birth_date);
                                    fieldContent = date.toLocaleDateString('de-DE');
                                  } catch (e) {
                                    fieldContent = examData.birth_date;
                                  }
                                } else {
                                  fieldContent = '[Geburtsdatum nicht verfügbar]';
                                }
                                break;
                              case 'sex':
                                fieldContent = examData?.sex || '[Geschlecht nicht verfügbar]';
                                break;
                              case 'modality':
                                fieldContent = examData?.modality || '[Modalität nicht verfügbar]';
                                break;
                              case 'study_description':
                                fieldContent = examData?.study_description || '[Studienbeschreibung nicht verfügbar]';
                                break;
                              case 'question':
                                fieldContent = examData?.question || '[Fragestellung nicht verfügbar]';
                                break;
                              case 'accession_number':
                                fieldContent = examData?.accession_number || '[Accession-Nummer nicht verfügbar]';
                                break;
                              case 'referring_physician':
                                fieldContent = examData?.referring_physician || '[Zuweiser nicht verfügbar]';
                                break;
                              case 'admission_id':
                                fieldContent = examData?.admission_id || '[Aufnahme-ID nicht verfügbar]';
                                break;
                              case 'comment':
                                fieldContent = examData?.comment || '[Kommentar nicht verfügbar]';
                                break;
                              case 'scan_protocol':
                                fieldContent = examData?.scan_protocol || '[Scan-Protokoll nicht verfügbar]';
                                break;
                              case 'first_reader':
                                fieldContent = examData?.first_reader || '[Erstbefunder nicht verfügbar]';
                                break;
                              case 'patientsAge':
                                fieldContent = examData?.patientsage || examData?.patientsAge || '[Patientenalter nicht verfügbar]';
                                break;
                              case 'patientsWeight':
                                fieldContent = examData?.patientsweight || examData?.patientsWeight || '[Patientengewicht nicht verfügbar]';
                                break;
                              case 'patientsSize':
                                fieldContent = examData?.patientssize || examData?.patientsSize || '[Patientengröße nicht verfügbar]';
                                break;
                              case 'occupation':
                                fieldContent = examData?.occupation || '[Beruf nicht verfügbar]';
                                break;
                              case 'additionalPatientsHistory':
                                fieldContent = examData?.additionalpatientshistory || examData?.additionalPatientsHistory || '[Zusätzliche Patientengeschichte nicht verfügbar]';
                                break;
                              case 'admittingDiagnosesDescription':
                                fieldContent = examData?.admittingdiagnosesdescription || examData?.admittingDiagnosesDescription || '[Aufnahmediagnose nicht verfügbar]';
                                break;
                              default:
                                const snakeCaseField = item.database_field.toLowerCase();
                                fieldContent = examData?.[snakeCaseField] || examData?.[item.database_field] || `[${item.database_field} nicht verfügbar]`;
                            }
                          } else {
                            // 📝 FREITEXT FIELD - aus template_data value laden mit HTML-Formatierung
                            console.log(`🔍 DEBUG FREITEXT FIELD: item.id="${item.id}", item.label="${item.label}"`);
                            console.log(`🔍 DEBUG FREITEXT FIELD: item.value="${item.value}"`);
                            
                            fieldContent = item.value || '[Kein Inhalt eingegeben]';
                            console.log(`🔍 DEBUG FREITEXT FIELD: fieldContent="${fieldContent}"`);
                            
                            // Inline-Kommentare entfernen, aber Formatierung behalten (rekursiv)
                            let cleanContent = fieldContent;
                            let previousContent = '';
                            
                            // Wiederhole bis keine data-teams spans mehr vorhanden (für verschachtelte spans)
                            while (cleanContent !== previousContent) {
                              previousContent = cleanContent;
                              cleanContent = cleanContent
                                .replace(/<span data-teams="true">(.*?)<\/span>/gs, '$1')
                                .replace(/<span data-teams=\"true\">(.*?)<\/span>/gs, '$1');
                            }
                            
                            fieldContent = cleanContent;
                            console.log(`🔍 DEBUG FREITEXT FIELD: cleanedContent="${fieldContent}"`);
                          }
                          
                          const isHtmlField = !item.database_field && item.value && item.value.includes('<');
                          
                          console.log(`🔍 DEBUG FIELD: fieldContent.trim()="${fieldContent.trim()}", length=${fieldContent.trim().length}`);
                          
                          if (fieldContent.trim()) {
                            console.log(`✅ DEBUG FIELD: Füge zu contentParts hinzu: label="${item.label}", isHtml=${isHtmlField}`);
                            contentParts.push({
                              label: item.label,
                              content: fieldContent,
                              isDatabase: !!item.database_field,
                              isHtml: isHtmlField
                            });
                          } else {
                            console.log(`❌ DEBUG FIELD: NICHT zu contentParts hinzugefügt (leer): label="${item.label}"`);
                          }
                          
                        } else if ((item.item_type === 'group' || item.type === 'group') && Array.isArray(item.findings)) {
                          // 🔥 STRUKTURIERTE GROUPS - Rendern wie in StructuredReporting.js
                          console.log(`📋 Strukturierte Group gefunden: ${item.name} mit ${item.findings.length} Findings`);
                          console.log(`🔍 DEBUG Group-Item:`, item);
                          console.log(`🔍 DEBUG Group-Findings:`, item.findings);
                          
                          let groupHTML = `<p style="margin: 0.8em 0 0.4em 0; padding: 0 0 0.2em 0; font-weight: bold;">${item.name || 'Gruppe'}</p>`;
                          
                          // Gruppiere Findings nach Name (EXAKT wie StructuredReporting.js)
                          const groupedByName = {};
                          item.findings.forEach((finding, index) => {
                            console.log(`🔍 DEBUG Finding ${index}:`, finding);
                            console.log(`🔍 DEBUG Finding.value:`, finding.value);
                            console.log(`🔍 DEBUG Finding.comment_value:`, finding.comment_value);
                            
                            const name = finding.name || 'Befund';
                            if (!groupedByName[name]) {
                              groupedByName[name] = [];
                            }
                            
                            // 🔥 KRITISCH: Hole die gespeicherten WERTE aus template_data!
                            // Die Findings haben ihre Werte in finding.value gespeichert
                            const findingWithValue = { 
                              ...finding, 
                              originalIndex: index,
                              // Verwende den gespeicherten Wert aus dem Finding
                              displayValue: finding.value || ''
                            };
                            
                            console.log(`🔍 DEBUG findingWithValue:`, findingWithValue);
                            
                            groupedByName[name].push(findingWithValue);
                          });
                          
                          console.log(`📊 Gruppierte Findings für Group "${item.name}":`, groupedByName);
                          
                          // Rendere grouped Findings als <p>-Elemente
                          Object.entries(groupedByName).forEach(([findingName, findingsWithSameName]) => {
                            if (findingsWithSameName.length === 1 && !findingsWithSameName[0].subtitle) {
                              // Einzelner Finding ohne Subtitle - direkt anzeigen
                              const finding = findingsWithSameName[0];
                              const value = finding.displayValue || '';
                              const unit = finding.unit || '';
                              
                              // 🔥 SPEZIAL-BEHANDLUNG für verschiedene Finding-Typen
                              let displayValue = '';
                              if (finding.finding_type === 'checkboxes' && Array.isArray(finding.options)) {
                                // Checkboxes: Zeige ausgewählte Optionen
                                if (value) {
                                  const selectedOptions = value.split(',').map(v => v.trim()).filter(v => v);
                                  displayValue = selectedOptions.length > 0 ? selectedOptions.join(', ') : '[Keine Auswahl]';
                                } else {
                                  displayValue = '[Keine Auswahl]';
                                }
                              } else if (finding.finding_type === 'dropdown' && Array.isArray(finding.options)) {
                                // Dropdown: Zeige ausgewählte Option
                                displayValue = value || '[Keine Auswahl]';
                              } else if (finding.finding_type === 'yesno_comment') {
                                // Yes/No mit Kommentar
                                if (value === 'yes' || value === 'no') {
                                  displayValue = value === 'yes' ? 'Ja' : 'Nein';
                                  // Prüfe auf Kommentar (könnte in separatem Feld gespeichert sein)
                                  const commentValue = finding.comment_value || '';
                                  if (commentValue.trim()) {
                                    displayValue += ` (${commentValue})`;
                                  }
                                } else {
                                  displayValue = value || '[Keine Auswahl]';
                                }
                              } else {
                                // Normale Felder (freetext, freetext_unit)
                                displayValue = unit ? `${value} ${unit}` : value;
                              }
                              
                              groupHTML += `<p style="margin: 0.2em 0; padding: 0 0 0 1em;"><strong>${findingName}:</strong> ${displayValue}</p>`;
                            } else {
                              // Mehrere Findings ODER mit Subtitle - verschachtelt anzeigen
                              // Name als Sub-Überschrift
                              groupHTML += `<p style="margin: 0.3em 0 0.2em 1em; padding: 0; font-weight: bold; color: #333;">${findingName}</p>`;
                              
                              // Findings darunter (stärkere Einrückung)
                              findingsWithSameName.forEach(finding => {
                                const subtitle = finding.subtitle || '';
                                const value = finding.displayValue || '';
                                const unit = finding.unit || '';
                                const label = subtitle || findingName;
                                
                                // 🔥 SPEZIAL-BEHANDLUNG für verschiedene Finding-Typen (wie oben)
                                let displayValue = '';
                                if (finding.finding_type === 'checkboxes' && Array.isArray(finding.options)) {
                                  if (value) {
                                    const selectedOptions = value.split(',').map(v => v.trim()).filter(v => v);
                                    displayValue = selectedOptions.length > 0 ? selectedOptions.join(', ') : '[Keine Auswahl]';
                                  } else {
                                    displayValue = '[Keine Auswahl]';
                                  }
                                } else if (finding.finding_type === 'dropdown' && Array.isArray(finding.options)) {
                                  displayValue = value || '[Keine Auswahl]';
                                } else if (finding.finding_type === 'yesno_comment') {
                                  if (value === 'yes' || value === 'no') {
                                    displayValue = value === 'yes' ? 'Ja' : 'Nein';
                                    const commentValue = finding.comment_value || '';
                                    if (commentValue.trim()) {
                                      displayValue += ` (${commentValue})`;
                                    }
                                  } else {
                                    displayValue = value || '[Keine Auswahl]';
                                  }
                                } else {
                                  displayValue = unit ? `${value} ${unit}` : value;
                                }
                                
                                groupHTML += `<p style="margin: 0.1em 0; padding: 0 0 0 2em;"><strong>${label}:</strong> ${displayValue}</p>`;
                              });
                            }
                          });
                          
                          // Ganze Group als EIN contentPart
                          contentParts.push({
                            label: '',
                            content: groupHTML,
                            isDatabase: false,
                            isHtml: true
                          });
                          
                          console.log(`✅ Group "${item.name}" als contentPart hinzugefügt mit ${Object.keys(groupedByName).length} Finding-Gruppen`);
                        }
                      });
                    }

                    // UNTERSCHRIFTEN AM ENDE DES BEFUNDS (nur wenn signiert)
                    if (pdfData.signers && pdfData.signers.length > 0 && pdfData.layout?.greeting_end) {
                      console.log('📝 Füge Unterschriften zum Befund hinzu:', pdfData.signers);
                      
                      // Baue HTML für Grußformel + Unterschriften-Grid
                      const greetingEnd = pdfData.layout.greeting_end;
                      
                      // Erstelle 3-Spalten-Grid für Unterschriften (automatisches Umbrechen auf mehrere Zeilen)
                      let signaturesHTML = `
                        <div style="margin-top: 2em;">
                          <p style="margin: 0; padding: 0;">${greetingEnd}</p>
                          <p style="margin: 0; padding: 0;">&nbsp;</p>
                          
                          <div style="display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; margin-top: 1em; grid-auto-rows: auto;">
                      `;
                      
                      // Füge jeden Unterzeichner hinzu (Grid bricht automatisch nach 3 Spalten um)
                      pdfData.signers.forEach((signer) => {
                        const { user_id, academic_degree, first_name, last_name, position, job_title } = signer;
                        
                        // Unterschriftsbild-URL (140x60 px) mit fixiertem Cache-Buster
                        const signatureUrl = createApiUrl(`profile/signature/${user_id}?t=${cacheBusterTimestamp}`);
                        
                        signaturesHTML += `
                          <div style="text-align: left;">
                            <img src="${signatureUrl}" 
                                 alt="Unterschrift ${first_name} ${last_name}" 
                                 style="width: 140px; height: 60px; display: block; margin: 0; object-fit: contain;" 
                                 onerror="this.style.display='none';" />
                            <p style="margin: 0.5em 0 0 0; font-size: 0.9em;">
                              ${position || '&nbsp;'}
                            </p>
                            <p style="margin: 0.2em 0 0 0; font-size: 0.9em; font-weight: bold;">
                              ${academic_degree || ''} ${first_name || ''} ${last_name || ''}
                            </p>
                            <p style="margin: 0.2em 0 0 0; font-size: 0.85em;">
                              ${job_title || ''}
                            </p>
                          </div>
                        `;
                      });
                      
                      signaturesHTML += `
                          </div>
                        </div>
                      `;
                      
                      // Elektronische Freigabe-Notiz hinzufügen (wenn verifiziert)
                      if (pdfData.approver) {
                        const { academic_degree, first_name, last_name, approved_at, approved_time } = pdfData.approver;
                        const approverTitle = academic_degree || '';
                        const approverName = `${first_name || ''} ${last_name || ''}`.trim();
                        const approvalDate = approved_at || '';
                        const approvalTime = approved_time || '';
                        
                        signaturesHTML += `
                          <p style="margin: 0; padding: 0;">&nbsp;</p>
                          <p style="margin: 0; padding: 0;">&nbsp;</p>
                          <p style="margin: 0; padding: 0; font-size: 0.9em; font-style: italic; text-align: left;">
                            ${t('pdfPreview.electronicApproval', {
                              title: approverTitle,
                              name: approverName,
                              date: approvalDate,
                              time: approvalTime
                            })}
                          </p>
                        `;
                        
                        console.log(`✅ Elektronische Freigabe-Notiz hinzugefügt: ${approverTitle} ${approverName} am ${approvalDate} um ${approvalTime}`);
                      }
                      
                      // Füge als letzten contentPart hinzu
                      contentParts.push({
                        label: '',
                        content: signaturesHTML,
                        isDatabase: false,
                        isHtml: true
                      });
                      
                      console.log(`✅ Unterschriften-Block mit ${pdfData.signers.length} Unterzeichner(n) zu contentParts hinzugefügt`);
                    }

                    return (
                      <Box
                        key="body"
                        sx={{
                          position: 'absolute',
                          top: `${y}mm`,
                          left: `${x}mm`,
                          width: `${width}mm`,
                          fontSize: `${fontSize}px`,
                          fontFamily: fontFamily,
                          lineHeight: `${lineHeight}px`,
                          pointerEvents: 'auto'
                        }}
                      >
                        <Box
                          sx={{
                            fontSize: `${fontSize}px`,
                            fontFamily: fontFamily,
                            lineHeight: `${lineHeight}px`,
                            margin: 0,
                            padding: 0,
                            color: 'black'
                          }}
                        >
                          {/* Gru├ƒformel am Anfang */}
                          {greetingText && (
                            <Typography
                              sx={{
                                fontSize: `${fontSize}px`,
                                fontFamily: fontFamily,
                                lineHeight: `${lineHeight}px`,
                                margin: 0,
                                padding: 0,
                                color: 'black',
                                whiteSpace: 'pre-line',
                                marginBottom: '1.5em'
                              }}
                            >
                              {greetingText}
                            </Typography>
                          )}
                          
                    {/* Text-Block mit intelligentem Clipping */}
                    <SmartTextRenderer 
                      contentParts={contentParts.map(part => ({
                        ...part,
                        content: highlightSearchTerms(part.content)
                      }))}
                      maxHeight={height}
                      maxWidth={width}
                      fontSize={fontSize}
                      fontFamily={fontFamily}
                      lineHeight={lineHeight}
                      greetingText={greetingText}
                      signaturesCount={pdfData.signers ? pdfData.signers.length : 0}
                      onContentMeasured={(measurement) => {
                        if (measurement.isOverflowing && measurement.remainingContent) {
                          setFollowingPages([{
                            pageNumber: 2,
                            remainingContent: measurement.remainingContent
                          }]);
                        } else {
                          setFollowingPages([]);
                        }
                      }}
                    />
                        </Box>
                      </Box>
                    );
                  })()}
                </Box>
              </Paper>
              
              {/* Folgeseiten (Page 2, 3, 4, ...) */}
              {followingPages.map((page, index) => {
                  const handleNextPageOverflow = (remainingContentForNextPage) => {
                    setFollowingPages(prevPages => {
                      // Pr├╝fe ob die n├ñchste Seite bereits existiert
                      const nextPageNumber = page.pageNumber + 1;
                      const pageExists = prevPages.some(p => p.pageNumber === nextPageNumber);
                      
                      if (pageExists) {
                        return prevPages; // Keine ├änderung
                      }
                      
                      return [
                        ...prevPages,
                        {
                          pageNumber: nextPageNumber,
                          remainingContent: remainingContentForNextPage
                        }
                      ];
                    });
                  };
                  
                  return (
                    <FollowingPage
                      key={`following-page-${page.pageNumber}`}
                      remainingContent={page.remainingContent}
                      layout={pdfData.layout}
                      backgroundImage={pdfData.background_images?.followingPages}
                      fontSize={11}
                      fontFamily={'"Segoe UI"'}
                      lineHeight={13.2}
                      pageNumber={page.pageNumber}
                      onNextPageOverflow={handleNextPageOverflow}
                      showWatermark={showWatermark}
                      watermarkText={watermarkText}
                      signaturesCount={0} // Keine Unterschriften auf Folgeseiten
                      highlightSearchTerms={highlightSearchTerms}
                    />
                  );
                })}
              
              {/* Addendum-Seiten (jedes Addendum auf separater Seite) */}
              {pdfData?.report?.addendum && (() => {
                try {
                  let addenda = pdfData.report.addendum;
                  // Falls als String gespeichert, parsen
                  if (typeof addenda === 'string') {
                    addenda = JSON.parse(addenda);
                  }
                  
                  if (Array.isArray(addenda) && addenda.length > 0) {
                    console.log(`📄 ${addenda.length} Addendum/Addenda gefunden für PDF-Rendering`);
                    
                    return addenda.map((addendum, addendumIndex) => {
                      // Nur approved Addenda anzeigen
                      if (!addendum.approved_by) {
                        console.log(`⏭️ Addendum ${addendumIndex + 1} ist noch nicht approved, überspringe`);
                        return null;
                      }
                      
                      console.log(`✅ Rendere Addendum ${addendumIndex + 1} auf separater Seite`);
                      
                      return (
                        <AddendumPage
                          key={`addendum-page-${addendumIndex}`}
                          addendum={addendum}
                          addendumIndex={addendumIndex}
                          layout={pdfData.layout}
                          backgroundImage={pdfData.background_images?.followingPages}
                          fontSize={11}
                          fontFamily={'"Segoe UI"'}
                          lineHeight={13.2}
                          showWatermark={showWatermark}
                          watermarkText={watermarkText}
                          greetingEnd={pdfData.layout?.greeting_end}
                          t={t}
                          createApiUrl={createApiUrl}
                          cacheBusterTimestamp={cacheBusterTimestamp}
                          highlightSearchTerms={highlightSearchTerms}
                        />
                      );
                    });
                  }
                } catch (e) {
                  console.error('❌ Fehler beim Rendern der Addenda:', e);
                }
                return null;
              })()}
              </Box>
            )}
          </Box>
        )}
      </DialogContent>

      {/* Dialog Actions */}
      <DialogActions sx={{ 
        borderTop: 1, 
        borderColor: 'divider',
        px: 3,
        py: 2,
        display: 'flex',
        gap: 1
      }}>
        {/* Paper-Zoom Controls */}
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mr: 'auto' }}>
          <Button
            onClick={() => {
              const newZoom = Math.max(0.3, paperZoom - 0.1);
              setPaperZoom(newZoom);
            }}
            size="small"
            variant="outlined"
          >
            -
          </Button>
          <Typography variant="caption" sx={{ minWidth: '50px', textAlign: 'center' }}>
            {(paperZoom * 100).toFixed(0)}%
          </Typography>
          <Button
            onClick={() => {
              const newZoom = Math.min(2, paperZoom + 0.1);
              setPaperZoom(newZoom);
            }}
            size="small"
            variant="outlined"
          >
            +
          </Button>
        </Box>

        <Button
          startIcon={<PrintIcon />}
            onClick={handleDownloadBrowserNative}
          disabled={!pdfData || loading}
          variant="contained"
          className="button"
        >
          {t('pdfPreview.print', 'Drucken')}
        </Button>

        <Button 
          onClick={handleClose}
          variant="outlined"
        >
          {t('pdfPreview.close', 'Schlie├ƒen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default PDFPreviewDialogStructured;

